/**
 * Elite Batch 5 Part 4 — Workflow Templates 408–427
 * E-commerce, Inventory, Loyalty & Marketplace workflows
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    {
      sendSmsDetails: { to, message },
    },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/crm/v3/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 5 PART 4 — TEMPLATES 408–427
// =============================================================================

export const ELITE_BATCH5_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 408. HIGH-SPEND CUSTOMER THANK-YOU PLUS SURPRISE (8 nodes)
  // ===================================================================

  "High-Spend Customer Thank-You Plus Surprise": {
    summary:
      "Celebrates high-spend milestones with personalized thank-you messages and surprise gifts. A spend milestone webhook triggers purchase history enrichment from HubSpot, AI-powered surprise selection, personalized thank-you message generation, thank-you email delivery, gift order creation in Shopify, loyalty tier upgrade, and response tracking in Sheets.",
    nodes: [
      trigger("Webhook: Spend milestone reached", "webhook_app", {
        source: "Payment Processor / CRM",
        event: "customer.spend_milestone",
        howItStarts:
          "A customer crosses a spend milestone threshold (e.g., $500, $1000, $5000 lifetime value)",
        setup:
          "Connect your payment processor or CRM to POST spend milestone events (customer ID, total spend, milestone tier) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Purchase history pull (HubSpot)
      hubspotNode(
        "2",
        "Pull purchase history from HubSpot",
        280,
        "GET",
        "objects/contacts/{customer_id}?properties=email,firstname,lastname,total_revenue,num_associated_deals,hs_lifecyclestage",
        [],
        [
          {
            name: "properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.total_revenue",
            customName: "total_spend",
            type: "number",
          },
          {
            name: "properties.num_associated_deals",
            customName: "order_count",
            type: "number",
          },
          {
            name: "properties.hs_lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 3. Surprise selection (runCode)
      n("3", "runCode", "Select surprise gift by spend tier", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select surprise type based on spend tier
const totalSpend = parseFloat(inputs.total_spend) || 0;
const orderCount = parseInt(inputs.order_count) || 0;
let surpriseTier, giftType, giftValue, loyaltyBonus;
if (totalSpend >= 5000) {
  surpriseTier = "platinum";
  giftType = "premium_gift_box";
  giftValue = 150;
  loyaltyBonus = 500;
} else if (totalSpend >= 2000) {
  surpriseTier = "gold";
  giftType = "curated_gift_set";
  giftValue = 75;
  loyaltyBonus = 250;
} else if (totalSpend >= 1000) {
  surpriseTier = "silver";
  giftType = "surprise_product";
  giftValue = 40;
  loyaltyBonus = 100;
} else {
  surpriseTier = "bronze";
  giftType = "discount_voucher";
  giftValue = 20;
  loyaltyBonus = 50;
}
return {
  surprise_tier: surpriseTier,
  gift_type: giftType,
  gift_value: giftValue,
  loyalty_bonus_points: loyaltyBonus,
  milestone_label: totalSpend >= 5000 ? "VIP Platinum" : totalSpend >= 2000 ? "Gold Member" : totalSpend >= 1000 ? "Silver Member" : "Valued Customer"
};`,
          outputVariables: [
            { name: "surprise_tier", type: "string" },
            { name: "gift_type", type: "string" },
            { name: "gift_value", type: "number" },
            { name: "loyalty_bonus_points", type: "number" },
            { name: "milestone_label", type: "string" },
          ],
        },
      }),

      // 4. AI thank-you message
      aiNode(
        "4",
        "AI generate thank-you message",
        760,
        "You are a premium customer experience writer. Create a warm, genuine thank-you message for a high-value customer reaching a spend milestone. Include specific appreciation for their loyalty, mention of their surprise gift, and a personal touch. Avoid being generic or overly salesy. Respond JSON: {subject_line: string, thank_you_body: string, gift_reveal_text: string, loyalty_upgrade_note: string}",
        "Customer: {first_name} {last_name} | Email: {customer_email} | Total Spend: ${total_spend} | Orders: {order_count} | Milestone: {milestone_label} | Surprise Tier: {surprise_tier} | Gift: {gift_type} (${gift_value} value) | Bonus Points: {loyalty_bonus_points}",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "thank_you_body", as: "thank_you_body", type: "string" },
          { field: "gift_reveal_text", as: "gift_reveal_text", type: "string" },
          {
            field: "loyalty_upgrade_note",
            as: "loyalty_upgrade_note",
            type: "string",
          },
        ],
      ),

      // 5. Thank-you email
      emailNode(
        "5",
        "Send thank-you email with surprise",
        1000,
        "{customer_email}",
        "{subject_line}",
        "{thank_you_body}\\n\\n{gift_reveal_text}\\n\\n{loyalty_upgrade_note}\\n\\nWith gratitude,\\nThe Team",
      ),

      // 6. Gift order creation (Shopify)
      shopifyNode(
        "6",
        "Create gift order in Shopify",
        1240,
        "POST",
        "orders.json",
        [
          {
            key: "order",
            value:
              '{"email":"{customer_email}","financial_status":"paid","note":"Loyalty surprise gift - {milestone_label}","tags":"surprise_gift,{surprise_tier}","line_items":[{"title":"Surprise Gift - {gift_type}","quantity":1,"price":"{gift_value}"}],"shipping_address":{"first_name":"{first_name}","last_name":"{last_name}"}}',
          },
        ],
        [
          { name: "order.id", customName: "gift_order_id", type: "string" },
          {
            name: "order.order_number",
            customName: "gift_order_number",
            type: "number",
          },
        ],
      ),

      // 7. Loyalty upgrade (HubSpot property update)
      hubspotNode(
        "7",
        "Upgrade loyalty tier in HubSpot",
        1480,
        "PATCH",
        "objects/contacts/{customer_id}",
        [
          {
            key: "properties",
            value:
              '{"loyalty_tier":"{surprise_tier}","loyalty_bonus_points":"{loyalty_bonus_points}","last_milestone":"{milestone_label}","last_surprise_date":"{today_date}"}',
          },
        ],
        [{ name: "id", customName: "updated_contact_id", type: "string" }],
      ),

      // 8. Response tracking (Sheets)
      sheetsNode(
        "8",
        "Log surprise to Sheets",
        1720,
        "SurpriseTracking",
        '[["{customer_email}","{first_name} {last_name}","{total_spend}","{order_count}","{milestone_label}","{surprise_tier}","{gift_type}","{gift_value}","{loyalty_bonus_points}","{gift_order_number}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 409. HIGH-TICKET BROWSE CONSULT OFFER (8 nodes)
  // ===================================================================

  "High-Ticket Browse Consult Offer": {
    summary:
      "Offers consultation to high-ticket product browsers showing deep purchase intent. A deep browse trigger fires decision-support verification logic, enriches the customer via HubSpot, generates a personalized consultation offer with AI, sends a booking-link email, creates a sales task, tracks bookings, and logs cooling-off suppression data to Sheets.",
    nodes: [
      trigger("Webhook: Deep browse on high-ticket product", "webhook_app", {
        source: "E-commerce Analytics / Segment",
        event: "browse.high_ticket_deep",
        howItStarts:
          "A visitor spends significant time browsing high-ticket products (3+ page views on items over a threshold, or 5+ minutes on a single product page)",
        setup:
          "Connect your analytics platform to POST deep-browse events (session ID, product URLs, time on page, visitor email if known, product prices) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Decision-support verification (runCode)
      n("2", "runCode", "Verify decision-support signals", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify the browse pattern indicates genuine high-ticket purchase consideration
const payload = JSON.parse(inputs.webhook_payload || '{}');
const products = payload.products_viewed || [];
const sessionMinutes = parseFloat(payload.time_on_site_minutes) || 0;
const pageViews = parseInt(payload.page_views) || 0;
const highTicketViews = products.filter(p => parseFloat(p.price) >= 500).length;
const maxPrice = Math.max(...products.map(p => parseFloat(p.price) || 0), 0);
const topProduct = products.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0))[0] || {};
const intentScore = Math.min(100, (highTicketViews * 15) + (sessionMinutes * 5) + (pageViews * 3));
const qualifiesForConsult = intentScore >= 40 && highTicketViews >= 2;
return {
  visitor_email: payload.email || "",
  top_product_name: topProduct.name || "Premium Product",
  top_product_price: maxPrice,
  high_ticket_views: highTicketViews,
  session_minutes: sessionMinutes,
  intent_score: intentScore,
  qualifies: qualifiesForConsult ? "yes" : "no",
  product_category: topProduct.category || "premium"
};`,
          outputVariables: [
            { name: "visitor_email", type: "string" },
            { name: "top_product_name", type: "string" },
            { name: "top_product_price", type: "number" },
            { name: "high_ticket_views", type: "number" },
            { name: "session_minutes", type: "number" },
            { name: "intent_score", type: "number" },
            { name: "qualifies", type: "string" },
            { name: "product_category", type: "string" },
          ],
        },
      }),

      // 3. Customer enrichment (HubSpot)
      hubspotNode(
        "3",
        "Enrich customer from HubSpot",
        520,
        "GET",
        "objects/contacts/{visitor_email}?idProperty=email&properties=firstname,lastname,phone,hs_lifecyclestage,num_associated_deals",
        [],
        [
          {
            name: "properties.firstname",
            customName: "contact_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "contact_last_name",
            type: "string",
          },
          {
            name: "properties.phone",
            customName: "contact_phone",
            type: "string",
          },
          {
            name: "properties.hs_lifecyclestage",
            customName: "contact_stage",
            type: "string",
          },
          { name: "id", customName: "hubspot_contact_id", type: "string" },
        ],
      ),

      // 4. AI consultation offer
      aiNode(
        "4",
        "AI generate consultation offer",
        760,
        "You are a luxury sales consultant. Write a helpful, non-pushy consultation offer for someone browsing high-ticket products. Position it as expert guidance to help them make the best decision — not a sales pitch. Include why a consultation helps for this product category, what the session covers, and how to book. Respond JSON: {email_subject: string, offer_body: string, consultation_benefits: string, booking_cta: string}",
        "Customer: {contact_first_name} {contact_last_name} | Product: {top_product_name} (${top_product_price}) | Category: {product_category} | Browse depth: {high_ticket_views} products, {session_minutes} min | Intent score: {intent_score}",
        [
          { field: "email_subject", as: "offer_subject", type: "string" },
          { field: "offer_body", as: "offer_body", type: "string" },
          {
            field: "consultation_benefits",
            as: "consultation_benefits",
            type: "string",
          },
          { field: "booking_cta", as: "booking_cta", type: "string" },
        ],
      ),

      // 5. Consult email with booking link
      emailNode(
        "5",
        "Send consultation offer email",
        1000,
        "{visitor_email}",
        "{offer_subject}",
        "Hi {contact_first_name},\\n\\n{offer_body}\\n\\n{consultation_benefits}\\n\\n{booking_cta}\\n\\nBook your free consultation: https://calendly.com/{team_slug}/product-consultation\\n\\nBest regards,\\nYour Product Expert",
      ),

      // 6. Sales task creation
      hubspotNode(
        "6",
        "Create sales task in HubSpot",
        1240,
        "POST",
        "objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Follow up: {contact_first_name} browsing {top_product_name}","hs_task_body":"High-intent browse session (score: {intent_score}). Viewed {high_ticket_views} high-ticket products. Top interest: {top_product_name} (${top_product_price}). Send consultation offer sent — follow up if no booking in 48h.","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH"}',
          },
        ],
        [{ name: "id", customName: "task_id", type: "string" }],
      ),

      // 7. Booking tracking delay + check
      n("7", "delayAction", "Wait 48h for booking response", 1480, {
        delayAction: { time: 48, timeFormat: "hours" },
      }),

      // 8. Cooling-off suppression logging (Sheets)
      sheetsNode(
        "8",
        "Log consult offer to Sheets",
        1720,
        "ConsultOffers",
        '[["{visitor_email}","{contact_first_name} {contact_last_name}","{top_product_name}","{top_product_price}","{intent_score}","{high_ticket_views}","{session_minutes}","{product_category}","{task_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 410. HOW DID WE DO FEEDBACK (8 nodes)
  // ===================================================================

  "How Did We Do Feedback": {
    summary:
      "Collects post-purchase CSAT feedback, routes by sentiment, and triggers advocacy, improvement, or escalation paths. A post-purchase delay triggers a CSAT email, captures responses, routes via sentiment condition — positive customers join advocacy, neutral get improvement prompts, negative cases create Zendesk tickets — with CX Slack alerts and trend logging.",
    nodes: [
      trigger("Webhook: Post-purchase CSAT trigger", "webhook_app", {
        source: "E-commerce Platform / Order System",
        event: "order.delivered",
        howItStarts:
          "An order is marked as delivered, triggering a post-purchase feedback request after a brief cooling-off period",
        setup:
          "Connect your order/fulfillment system to POST delivery confirmation events (order ID, customer email, product names, delivery date) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Post-purchase delay + CSAT email
      n("2", "delayAction", "Wait 3 days post-delivery", 280, {
        delayAction: { time: 3, timeFormat: "days" },
      }),

      // 3. CSAT email
      emailNode(
        "3",
        "Send CSAT feedback email",
        520,
        "{customer_email}",
        "How did we do? We would love your feedback on your recent order",
        "Hi {customer_name},\\n\\nYour recent order ({order_id}) has been delivered. We would love to hear how everything went!\\n\\nPlease rate your experience:\\n\\nRate us: {feedback_form_url}?order={order_id}&email={customer_email}\\n\\nYour feedback helps us improve and serve you better.\\n\\nThank you,\\nCustomer Experience Team",
      ),

      // 4. Response capture + sentiment analysis
      aiNode(
        "4",
        "AI analyze feedback sentiment",
        760,
        "You are a customer experience analyst. Analyze the CSAT feedback response and determine sentiment category. Consider the rating score, free-text comments, and overall tone. Categorize as: positive (score 4-5, happy tone), neutral (score 3, mixed tone), or negative (score 1-2, unhappy tone). Extract specific praise or complaints. Respond JSON: {sentiment: string, rating_score: number, key_feedback: string, specific_praise: string, specific_complaints: string, improvement_area: string, advocacy_potential: string}",
        "Order: {order_id} | Customer: {customer_name} | Feedback payload: {webhook_payload}",
        [
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "rating_score", as: "rating_score", type: "number" },
          { field: "key_feedback", as: "key_feedback", type: "string" },
          { field: "specific_praise", as: "specific_praise", type: "string" },
          {
            field: "specific_complaints",
            as: "specific_complaints",
            type: "string",
          },
          { field: "improvement_area", as: "improvement_area", type: "string" },
          {
            field: "advocacy_potential",
            as: "advocacy_potential",
            type: "string",
          },
        ],
      ),

      // 5. Sentiment routing condition
      n("5", "condition", "Sentiment is positive?", 1000, {
        conditionData: [
          {
            variable: "sentiment",
            value: "positive",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Positive: advocacy path email
      emailNode(
        "6",
        "Positive: Send advocacy invitation",
        1240,
        "{customer_email}",
        "Thank you for your amazing feedback! Join our referral program",
        "Hi {customer_name},\\n\\nWe are thrilled you had a great experience! Your feedback means the world to us.\\n\\nWe would love for you to share the love — join our referral program and earn rewards for every friend you bring:\\n{referral_program_url}\\n\\nLeave a review: {review_url}\\n\\nThank you for being an incredible customer!",
        460,
      ),

      // 7. Negative: Zendesk ticket creation
      n(
        "7",
        "hitApi",
        "Negative: Create Zendesk support ticket",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"subject":"Negative CSAT feedback - Order {order_id}","description":"Customer {customer_name} ({customer_email}) rated their experience {rating_score}/5.\\n\\nFeedback: {key_feedback}\\nComplaints: {specific_complaints}\\nArea: {improvement_area}","priority":"high","tags":["csat_negative","auto_escalation"],"requester":{"email":"{customer_email}","name":"{customer_name}"}}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "zendesk_ticket_id",
                type: "string",
              },
            ],
          },
        },
        460,
      ),

      // 8. CX Slack alert + trend logging (Sheets)
      slackNode(
        "8",
        "Slack: CX team feedback alert",
        1480,
        "CSAT Feedback Received\\nCustomer: {customer_name} | Order: {order_id}\\nRating: {rating_score}/5 | Sentiment: {sentiment}\\nFeedback: {key_feedback}\\nPraise: {specific_praise}\\nComplaints: {specific_complaints}\\nAdvocacy potential: {advocacy_potential}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // positive -> advocacy
      e("5", "7", "output_2"), // negative -> Zendesk ticket
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 411. INTELLECTUAL PROPERTY INVENTORY BOT (8 nodes)
  // ===================================================================

  "Intellectual Property Inventory Bot": {
    summary:
      "Automates IP question answering and violation detection. An IP inquiry or violation webhook triggers registry search, AI policy response generation, marketplace comparison logic, violation confidence scoring, legal escalation via Slack for high-confidence cases, formal response email, and IP audit logging in Sheets.",
    nodes: [
      trigger("Webhook: IP question or violation report", "webhook_app", {
        source: "Legal Portal / Brand Protection Tool / Chat",
        event: "ip.inquiry_or_violation",
        howItStarts:
          "Someone submits an IP question (trademark lookup, copyright clarification) or reports a potential IP violation (counterfeit listing, unauthorized use)",
        setup:
          "Connect your legal portal, brand protection monitoring tool, or internal chat to POST IP inquiries (query text, reporter info, suspected listing URLs) to the workflow URL.",
        inputFields: [],
      }),

      // 2. IP registry search
      n("2", "hitApi", "Search IP registry database", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.ipregistry.example.com/v1/search",
          headers: [
            { key: "Authorization", value: "Bearer {ip_registry_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "query", value: "{webhook_payload}" },
            { key: "search_type", value: "trademark_copyright" },
            { key: "include_history", value: "true" },
          ],
          variables: [
            { name: "results", customName: "registry_results", type: "string" },
            {
              name: "match_count",
              customName: "registry_match_count",
              type: "number",
            },
            {
              name: "top_match.registration_number",
              customName: "registration_number",
              type: "string",
            },
            { name: "top_match.owner", customName: "ip_owner", type: "string" },
            {
              name: "top_match.status",
              customName: "ip_status",
              type: "string",
            },
            { name: "top_match.type", customName: "ip_type", type: "string" },
          ],
        },
      }),

      // 3. AI policy answer
      aiNode(
        "3",
        "AI generate IP policy response",
        520,
        "You are an intellectual property specialist. Given the IP inquiry/report and registry search results, provide a clear policy-based response. For questions: explain the IP status, ownership, and relevant protections. For violations: assess the strength of the claim, recommend next steps, and draft a preliminary response. Always cite the registration number and owner. Respond JSON: {response_type: string, policy_answer: string, violation_likelihood: string, recommended_action: string, citation: string, urgency: string, reporter_name: string, reporter_email: string, suspected_url: string}",
        "Inquiry: {webhook_payload} | Registry matches: {registry_match_count} | Top match: {registration_number} owned by {ip_owner} | Status: {ip_status} | Type: {ip_type} | Full results: {registry_results}",
        [
          { field: "response_type", as: "response_type", type: "string" },
          { field: "policy_answer", as: "policy_answer", type: "string" },
          {
            field: "violation_likelihood",
            as: "violation_likelihood",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "citation", as: "citation", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "reporter_name", as: "reporter_name", type: "string" },
          { field: "reporter_email", as: "reporter_email", type: "string" },
          { field: "suspected_url", as: "suspected_url", type: "string" },
        ],
      ),

      // 4. Marketplace comparison (runCode)
      n("4", "runCode", "Compare against marketplace listings", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate violation confidence based on registry match + AI analysis
const violationLikelihood = inputs.violation_likelihood || "low";
const registryMatches = parseInt(inputs.registry_match_count) || 0;
const urgency = inputs.urgency || "low";
const likelihoodScores = { high: 80, medium: 50, low: 20 };
let confidenceScore = likelihoodScores[violationLikelihood] || 20;
if (registryMatches > 0) confidenceScore += 15;
if (urgency === "high") confidenceScore += 10;
confidenceScore = Math.min(100, confidenceScore);
const isHighConfidence = confidenceScore >= 70;
return {
  violation_confidence: confidenceScore,
  needs_legal_escalation: isHighConfidence ? "yes" : "no",
  confidence_label: confidenceScore >= 70 ? "high" : confidenceScore >= 40 ? "medium" : "low"
};`,
          outputVariables: [
            { name: "violation_confidence", type: "number" },
            { name: "needs_legal_escalation", type: "string" },
            { name: "confidence_label", type: "string" },
          ],
        },
      }),

      // 5. Violation confidence condition
      n("5", "condition", "High-confidence violation?", 1000, {
        conditionData: [
          {
            variable: "needs_legal_escalation",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Legal escalation (Slack)
      slackNode(
        "6",
        "Slack: Escalate to legal team",
        1240,
        "IP VIOLATION ALERT (Confidence: {violation_confidence}%)\\nType: {response_type} | IP: {ip_type}\\nOwner: {ip_owner} | Registration: {registration_number}\\nSuspected URL: {suspected_url}\\nLikelihood: {violation_likelihood} | Urgency: {urgency}\\nRecommended action: {recommended_action}\\nReporter: {reporter_name} ({reporter_email})",
        460,
      ),

      // 7. Formal response email
      emailNode(
        "7",
        "Send formal IP response",
        1480,
        "{reporter_email}",
        "IP Inquiry Response: {ip_type} - {registration_number}",
        "Dear {reporter_name},\\n\\nThank you for your IP inquiry. We have reviewed your request against our IP registry and marketplace data.\\n\\n{policy_answer}\\n\\nCitation: {citation}\\n\\nRecommended next steps: {recommended_action}\\n\\nOur legal team has been notified and will follow up if further action is required.\\n\\nBest regards,\\nIP Compliance Team",
      ),

      // 8. IP audit logging (Sheets)
      sheetsNode(
        "8",
        "Log IP case to Sheets",
        1720,
        "IPAudit",
        '[["{reporter_name}","{reporter_email}","{response_type}","{ip_type}","{ip_owner}","{registration_number}","{violation_likelihood}","{violation_confidence}","{confidence_label}","{recommended_action}","{suspected_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high confidence -> legal escalation
      e("5", "7", "output_2"), // low confidence -> skip escalation, send response
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 412. INVENTORY ALERT WORKFLOW (8 nodes)
  // ===================================================================

  "Inventory Alert Workflow": {
    summary:
      "Monitors inventory changes, alerts ops on low stock, and auto-generates reorder recommendations. An inventory webhook triggers threshold comparison logic, a low-stock condition check, Slack alerts for ops, critical escalation for near-zero stock, AI reorder recommendations, supplier PO notification emails, and storefront indicator updates plus logging.",
    nodes: [
      trigger("Webhook: Inventory level change", "webhook_app", {
        source: "Shopify / Warehouse Management System",
        event: "inventory.level_change",
        howItStarts:
          "An inventory level changes due to a sale, return, adjustment, or sync from the warehouse management system",
        setup:
          "Connect your Shopify inventory webhooks or WMS to POST inventory change events (product ID, SKU, variant ID, available quantity, location) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Threshold comparison (runCode)
      n("2", "runCode", "Compare against reorder thresholds", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare current inventory against thresholds
const payload = JSON.parse(inputs.webhook_payload || '{}');
const sku = payload.sku || payload.variant_id || "unknown";
const productName = payload.product_title || payload.name || "Unknown Product";
const available = parseInt(payload.available) || parseInt(payload.inventory_quantity) || 0;
const location = payload.location_name || payload.location_id || "Primary";
// Default thresholds (in production, pull from config)
const criticalThreshold = 5;
const lowThreshold = 20;
const reorderPoint = 30;
let stockStatus;
if (available <= 0) stockStatus = "out_of_stock";
else if (available <= criticalThreshold) stockStatus = "critical";
else if (available <= lowThreshold) stockStatus = "low";
else if (available <= reorderPoint) stockStatus = "reorder_point";
else stockStatus = "healthy";
return {
  sku: sku,
  product_name: productName,
  available_qty: available,
  location: location,
  stock_status: stockStatus,
  is_low_stock: (available <= lowThreshold) ? "yes" : "no",
  is_critical: (available <= criticalThreshold) ? "yes" : "no",
  days_of_supply: Math.round(available / (payload.avg_daily_sales || 3))
};`,
          outputVariables: [
            { name: "sku", type: "string" },
            { name: "product_name", type: "string" },
            { name: "available_qty", type: "number" },
            { name: "location", type: "string" },
            { name: "stock_status", type: "string" },
            { name: "is_low_stock", type: "string" },
            { name: "is_critical", type: "string" },
            { name: "days_of_supply", type: "number" },
          ],
        },
      }),

      // 3. Low-stock condition
      n("3", "condition", "Is stock low or critical?", 520, {
        conditionData: [
          {
            variable: "is_low_stock",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Ops Slack alert
      slackNode(
        "4",
        "Slack: Low stock alert to ops",
        760,
        "LOW STOCK ALERT\\nProduct: {product_name} (SKU: {sku})\\nAvailable: {available_qty} units | Status: {stock_status}\\nLocation: {location} | Days of supply: {days_of_supply}\\nAction required: Review and consider reorder",
      ),

      // 5. Critical escalation (additional urgency for near-zero)
      n("5", "condition", "Is stock critical (<= 5)?", 1000, {
        conditionData: [
          {
            variable: "is_critical",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI reorder recommendation
      aiNode(
        "6",
        "AI generate reorder recommendation",
        1240,
        "You are an inventory management specialist. Given current stock levels, days of supply, and product info, recommend the optimal reorder quantity, suggested supplier priority, and estimated lead time. Consider seasonal patterns and stockout risk. Respond JSON: {reorder_qty: number, recommended_supplier: string, lead_time_days: number, priority_level: string, reorder_justification: string, cost_estimate: string}",
        "Product: {product_name} | SKU: {sku} | Available: {available_qty} | Status: {stock_status} | Days of supply: {days_of_supply} | Location: {location}",
        [
          { field: "reorder_qty", as: "reorder_qty", type: "number" },
          {
            field: "recommended_supplier",
            as: "recommended_supplier",
            type: "string",
          },
          { field: "lead_time_days", as: "lead_time_days", type: "number" },
          { field: "priority_level", as: "priority_level", type: "string" },
          {
            field: "reorder_justification",
            as: "reorder_justification",
            type: "string",
          },
          { field: "cost_estimate", as: "cost_estimate", type: "string" },
        ],
      ),

      // 7. Supplier PO notification
      emailNode(
        "7",
        "Email supplier: PO notification",
        1480,
        "{supplier_email}",
        "Purchase Order Request: {product_name} (SKU: {sku}) - Qty: {reorder_qty}",
        "Dear {recommended_supplier},\\n\\nWe are requesting a purchase order for the following:\\n\\nProduct: {product_name}\\nSKU: {sku}\\nQuantity: {reorder_qty} units\\nPriority: {priority_level}\\nEstimated lead time needed: {lead_time_days} days\\n\\nJustification: {reorder_justification}\\nEstimated cost: {cost_estimate}\\n\\nPlease confirm availability and expected ship date.\\n\\nThank you,\\nOperations Team",
      ),

      // 8. Storefront indicator update + logging (Sheets)
      sheetsNode(
        "8",
        "Log inventory alert to Sheets",
        1720,
        "InventoryAlerts",
        '[["{sku}","{product_name}","{available_qty}","{stock_status}","{days_of_supply}","{location}","{reorder_qty}","{recommended_supplier}","{priority_level}","{cost_estimate}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // low stock -> alert
      e("3", "8", "output_2"), // healthy stock -> just log
      e("4", "5"),
      e("5", "6", "output_1"), // critical -> AI reorder + supplier PO
      e("5", "8", "output_2"), // low but not critical -> just log
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 413. INVENTORY DATA RECONCILIATION (9 nodes)
  // ===================================================================

  "Inventory Data Reconciliation": {
    summary:
      "Reconciles inventory across multiple sources to catch and correct discrepancies. A scheduled trigger pulls data from Shopify and a secondary source, matches SKUs via runCode, detects discrepancies, uses AI for pattern analysis, auto-corrects or alerts ops via condition, logs corrections to Sheets, and creates a finance audit trail.",
    nodes: [
      trigger("Daily inventory reconciliation schedule", "schedule", {
        schedule: "Every day at 2:00 AM UTC",
        howItStarts:
          "Runs daily to compare inventory quantities between Shopify and the warehouse management system to detect and resolve discrepancies",
        setup:
          "Set the cron schedule for daily reconciliation. Ensure Shopify and WMS API credentials are configured.",
        inputFields: [],
      }),

      // 2. Shopify inventory pull
      shopifyNode(
        "2",
        "Pull Shopify inventory levels",
        280,
        "GET",
        "inventory_levels.json?limit=250",
        [],
        [
          {
            name: "inventory_levels",
            customName: "shopify_inventory",
            type: "string",
          },
          {
            name: "inventory_levels.length",
            customName: "shopify_sku_count",
            type: "number",
          },
        ],
      ),

      // 3. Secondary source pull (WMS)
      n("3", "hitApi", "Pull WMS inventory levels", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.warehouse.example.com/v1/inventory?include_all=true",
          headers: [
            { key: "Authorization", value: "Bearer {wms_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "items", customName: "wms_inventory", type: "string" },
            {
              name: "total_count",
              customName: "wms_sku_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. SKU matching (runCode)
      n("4", "runCode", "Match SKUs and detect discrepancies", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Match SKUs between Shopify and WMS, find discrepancies
const shopify = JSON.parse(inputs.shopify_inventory || '[]');
const wms = JSON.parse(inputs.wms_inventory || '[]');
const wmsMap = {};
wms.forEach(item => { wmsMap[item.sku || item.inventory_item_id] = item; });
const discrepancies = [];
let matchedCount = 0;
let missingInWms = 0;
shopify.forEach(sItem => {
  const key = sItem.sku || sItem.inventory_item_id;
  const wmsItem = wmsMap[key];
  if (!wmsItem) {
    missingInWms++;
    discrepancies.push({ sku: key, shopify_qty: sItem.available, wms_qty: 0, diff: sItem.available, type: "missing_in_wms" });
  } else {
    matchedCount++;
    const diff = Math.abs((sItem.available || 0) - (wmsItem.quantity || 0));
    if (diff > 0) {
      discrepancies.push({ sku: key, shopify_qty: sItem.available, wms_qty: wmsItem.quantity, diff: diff, type: "quantity_mismatch" });
    }
  }
});
const discrepancyCount = discrepancies.length;
const canAutoCorrect = discrepancies.every(d => d.diff <= 5 && d.type === "quantity_mismatch");
return {
  matched_skus: matchedCount,
  discrepancy_count: discrepancyCount,
  missing_in_wms: missingInWms,
  discrepancy_data: JSON.stringify(discrepancies.slice(0, 50)),
  has_discrepancies: discrepancyCount > 0 ? "yes" : "no",
  auto_correctable: canAutoCorrect ? "yes" : "no",
  max_diff: Math.max(...discrepancies.map(d => d.diff), 0)
};`,
          outputVariables: [
            { name: "matched_skus", type: "number" },
            { name: "discrepancy_count", type: "number" },
            { name: "missing_in_wms", type: "number" },
            { name: "discrepancy_data", type: "string" },
            { name: "has_discrepancies", type: "string" },
            { name: "auto_correctable", type: "string" },
            { name: "max_diff", type: "number" },
          ],
        },
      }),

      // 5. AI pattern analysis
      aiNode(
        "5",
        "AI analyze discrepancy patterns",
        1000,
        "You are an inventory data analyst. Analyze the discrepancies between Shopify and the warehouse management system. Identify patterns (systematic drift, specific categories, location issues, timing patterns). Recommend root cause and corrective actions. Respond JSON: {pattern_summary: string, likely_root_cause: string, corrective_actions: string, risk_level: string, auto_correct_safe: string}",
        "Matched SKUs: {matched_skus} | Discrepancies: {discrepancy_count} | Missing in WMS: {missing_in_wms} | Max diff: {max_diff} | Auto-correctable: {auto_correctable} | Data: {discrepancy_data}",
        [
          { field: "pattern_summary", as: "pattern_summary", type: "string" },
          {
            field: "likely_root_cause",
            as: "likely_root_cause",
            type: "string",
          },
          {
            field: "corrective_actions",
            as: "corrective_actions",
            type: "string",
          },
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "auto_correct_safe",
            as: "auto_correct_safe",
            type: "string",
          },
        ],
      ),

      // 6. Auto-correct condition
      n("6", "condition", "Safe to auto-correct?", 1240, {
        conditionData: [
          {
            variable: "auto_correct_safe",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "auto_correctable",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Ops Slack alert
      slackNode(
        "7",
        "Slack: Inventory reconciliation report",
        1480,
        "INVENTORY RECONCILIATION REPORT\\nMatched: {matched_skus} SKUs | Discrepancies: {discrepancy_count} | Missing in WMS: {missing_in_wms}\\nRisk level: {risk_level} | Auto-correctable: {auto_correctable}\\nRoot cause: {likely_root_cause}\\nActions: {corrective_actions}\\nPattern: {pattern_summary}",
      ),

      // 8. Correction logging (Sheets)
      sheetsNode(
        "8",
        "Log corrections to Sheets",
        1720,
        "InventoryReconciliation",
        '[["{matched_skus}","{discrepancy_count}","{missing_in_wms}","{max_diff}","{auto_correctable}","{risk_level}","{likely_root_cause}","{corrective_actions}","{pattern_summary}"]]',
      ),

      // 9. Finance audit trail
      sheetsNode(
        "9",
        "Log finance audit trail",
        1960,
        "FinanceAudit",
        '[["{matched_skus}","{discrepancy_count}","{auto_correctable}","{risk_level}","{likely_root_cause}","{corrective_actions}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // safe to auto-correct -> alert with auto-correct note
      e("6", "7", "output_2"), // not safe -> alert requiring manual action
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 414. INVENTORY-URGENCY CART RECOVERY (7 nodes)
  // ===================================================================

  "Inventory-Urgency Cart Recovery": {
    summary:
      "Recovers abandoned carts using real inventory urgency signals. A cart abandon trigger checks live Shopify stock, classifies urgency via condition, sends real-urgency email, escalates to SMS for critical stock, handles mid-sequence sellouts, and logs stock-vs-conversion data to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "E-commerce Platform / Shopify",
        event: "cart.abandoned",
        howItStarts:
          "A customer abandons their cart after adding items, triggering a recovery sequence that uses real inventory data for authentic urgency",
        setup:
          "Connect your e-commerce platform to POST cart abandonment events (cart ID, customer email, line items with variant IDs, cart total) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Live stock check (Shopify)
      n("2", "runCode", "Check live stock for cart items", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse cart data and simulate stock classification
const payload = JSON.parse(inputs.webhook_payload || '{}');
const customerEmail = payload.email || payload.customer_email || "";
const customerName = payload.customer_name || payload.first_name || "there";
const cartTotal = parseFloat(payload.total_price) || 0;
const items = payload.line_items || payload.items || [];
let lowestStock = Infinity;
let criticalItems = 0;
let lowItems = 0;
const itemSummaries = [];
items.forEach(item => {
  const qty = parseInt(item.inventory_quantity || item.available) || 50;
  if (qty < lowestStock) lowestStock = qty;
  if (qty <= 3) criticalItems++;
  else if (qty <= 15) lowItems++;
  itemSummaries.push(item.title || item.name || "Item");
});
if (lowestStock === Infinity) lowestStock = 50;
let stockClass;
if (criticalItems > 0) stockClass = "critical";
else if (lowItems > 0) stockClass = "low";
else stockClass = "normal";
return {
  customer_email: customerEmail,
  customer_name: customerName,
  cart_total: cartTotal,
  item_names: itemSummaries.join(", "),
  lowest_stock: lowestStock,
  critical_item_count: criticalItems,
  stock_classification: stockClass,
  is_critical_stock: stockClass === "critical" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "item_names", type: "string" },
            { name: "lowest_stock", type: "number" },
            { name: "critical_item_count", type: "number" },
            { name: "stock_classification", type: "string" },
            { name: "is_critical_stock", type: "string" },
          ],
        },
      }),

      // 3. Stock classification condition
      n("3", "condition", "Stock is low or critical?", 520, {
        conditionData: [
          {
            variable: "stock_classification",
            value: "normal",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Real-urgency email
      emailNode(
        "4",
        "Send urgency-based recovery email",
        760,
        "{customer_email}",
        "Your cart items are selling fast — only {lowest_stock} left",
        "Hi {customer_name},\\n\\nYou left some great items in your cart ({item_names}), and we wanted to let you know — stock is running low.\\n\\nYour cart total: ${cart_total}\\nLowest stock remaining: {lowest_stock} units\\n\\nThis is not a marketing tactic — these items genuinely have limited availability. Complete your purchase before they sell out:\\n\\n{cart_recovery_url}\\n\\nHappy shopping!",
      ),

      // 5. SMS for critical (Twilio)
      smsNode(
        "5",
        "SMS: Critical stock recovery alert",
        1000,
        "{customer_phone}",
        "Heads up {customer_name}! Items in your cart ({item_names}) are nearly sold out — only {lowest_stock} left. Complete your order: {cart_recovery_url}",
      ),

      // 6. Mid-sequence sellout handling delay
      n("6", "delayAction", "Wait 2h before sellout check", 1240, {
        delayAction: { time: 2, timeFormat: "hours" },
      }),

      // 7. Stock vs conversion logging (Sheets)
      sheetsNode(
        "7",
        "Log cart recovery to Sheets",
        1480,
        "CartRecoveryInventory",
        '[["{customer_email}","{customer_name}","{cart_total}","{item_names}","{lowest_stock}","{stock_classification}","{critical_item_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // low/critical stock -> urgency email
      e("3", "7", "output_2"), // normal stock -> just log (no fake urgency)
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 415. INVENTORY-TO-AD BUDGET SYNC (8 nodes)
  // ===================================================================

  "Inventory-to-Ad Budget Sync": {
    summary:
      "Syncs ad spend with inventory levels to prevent wasted budget on out-of-stock products. An inventory change trigger classifies stock levels, maps to ad campaigns, generates budget recommendations, routes through approval for large changes, pauses or reactivates Google Ads campaigns, logs waste prevention data, and monitors reactivation readiness.",
    nodes: [
      trigger("Webhook: Inventory level change", "webhook_app", {
        source: "Shopify / Inventory Management System",
        event: "inventory.level_updated",
        howItStarts:
          "An inventory level change is detected that may require ad budget adjustment — either stock dropping below threshold or replenishment arriving",
        setup:
          "Connect your inventory system to POST inventory change events (product ID, SKU, available quantity, previous quantity, product title) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Stock classification (runCode)
      n("2", "runCode", "Classify stock level for ad impact", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify stock and determine ad budget action
const payload = JSON.parse(inputs.webhook_payload || '{}');
const sku = payload.sku || "unknown";
const productName = payload.product_title || "Unknown";
const available = parseInt(payload.available) || 0;
const previous = parseInt(payload.previous_quantity) || available;
const direction = available > previous ? "restocked" : "depleting";
let adAction, budgetMultiplier;
if (available <= 0) { adAction = "pause"; budgetMultiplier = 0; }
else if (available <= 5) { adAction = "reduce_heavy"; budgetMultiplier = 0.1; }
else if (available <= 20) { adAction = "reduce_light"; budgetMultiplier = 0.5; }
else if (available > 20 && direction === "restocked") { adAction = "reactivate"; budgetMultiplier = 1.0; }
else { adAction = "maintain"; budgetMultiplier = 1.0; }
return {
  sku: sku,
  product_name: productName,
  available_qty: available,
  previous_qty: previous,
  stock_direction: direction,
  ad_action: adAction,
  budget_multiplier: budgetMultiplier,
  needs_approval: (adAction === "pause" || adAction === "reactivate") ? "yes" : "no"
};`,
          outputVariables: [
            { name: "sku", type: "string" },
            { name: "product_name", type: "string" },
            { name: "available_qty", type: "number" },
            { name: "previous_qty", type: "number" },
            { name: "stock_direction", type: "string" },
            { name: "ad_action", type: "string" },
            { name: "budget_multiplier", type: "number" },
            { name: "needs_approval", type: "string" },
          ],
        },
      }),

      // 3. Campaign mapping
      aiNode(
        "3",
        "AI map product to ad campaigns",
        520,
        "You are a digital advertising operations assistant. Given a product SKU and name, identify which Google Ads campaigns, ad groups, and keywords should be adjusted. Map the product to relevant campaign IDs based on naming conventions. Respond JSON: {campaign_ids: string, ad_group_ids: string, affected_keywords: string, campaign_names: string, current_daily_budget: number, recommended_daily_budget: number}",
        "Product: {product_name} | SKU: {sku} | Action needed: {ad_action} | Budget multiplier: {budget_multiplier} | Stock: {available_qty} units | Direction: {stock_direction}",
        [
          { field: "campaign_ids", as: "campaign_ids", type: "string" },
          { field: "ad_group_ids", as: "ad_group_ids", type: "string" },
          { field: "campaign_names", as: "campaign_names", type: "string" },
          {
            field: "current_daily_budget",
            as: "current_daily_budget",
            type: "number",
          },
          {
            field: "recommended_daily_budget",
            as: "recommended_daily_budget",
            type: "number",
          },
        ],
      ),

      // 4. Budget recommendation
      n("4", "runCode", "Calculate budget adjustment", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate final budget recommendation
const currentBudget = parseFloat(inputs.current_daily_budget) || 50;
const multiplier = parseFloat(inputs.budget_multiplier) || 1.0;
const newBudget = Math.round(currentBudget * multiplier * 100) / 100;
const savingsPerDay = Math.max(0, currentBudget - newBudget);
const savingsPerMonth = Math.round(savingsPerDay * 30 * 100) / 100;
return {
  new_daily_budget: newBudget,
  daily_savings: savingsPerDay,
  monthly_savings: savingsPerMonth,
  budget_change_pct: Math.round((multiplier - 1) * 100)
};`,
          outputVariables: [
            { name: "new_daily_budget", type: "number" },
            { name: "daily_savings", type: "number" },
            { name: "monthly_savings", type: "number" },
            { name: "budget_change_pct", type: "number" },
          ],
        },
      }),

      // 5. Ad ops approval condition
      n("5", "condition", "Needs ad ops approval?", 1000, {
        conditionData: [
          {
            variable: "needs_approval",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Campaign pause/reactivate (Google Ads API)
      n("6", "hitApi", "Google Ads: Update campaign budget", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/campaignBudgets:mutate",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
          ],
          body: [
            {
              key: "operations",
              value:
                '[{"update":{"resourceName":"customers/{google_ads_customer_id}/campaignBudgets/{campaign_ids}","amountMicros":"{new_daily_budget}000000"},"updateMask":"amountMicros"}]',
            },
          ],
          variables: [
            {
              name: "results[0].resourceName",
              customName: "updated_budget_resource",
              type: "string",
            },
          ],
        },
      }),

      // 7. Waste prevention logging (Sheets)
      sheetsNode(
        "7",
        "Log ad budget sync to Sheets",
        1480,
        "AdBudgetSync",
        '[["{sku}","{product_name}","{available_qty}","{stock_direction}","{ad_action}","{campaign_names}","{current_daily_budget}","{new_daily_budget}","{daily_savings}","{monthly_savings}","{budget_change_pct}"]]',
      ),

      // 8. Reactivation monitoring
      slackNode(
        "8",
        "Slack: Ad budget sync notification",
        1720,
        "AD BUDGET SYNC\\nProduct: {product_name} (SKU: {sku})\\nStock: {available_qty} units ({stock_direction})\\nAction: {ad_action} | Campaigns: {campaign_names}\\nBudget: ${current_daily_budget}/day -> ${new_daily_budget}/day ({budget_change_pct}%)\\nMonthly savings: ${monthly_savings}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // needs approval (pause/reactivate) -> update
      e("5", "7", "output_2"), // no approval needed (small changes) -> just log
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 416. KLAVIYO ABANDONED CART RECOVERY (8 nodes)
  // ===================================================================

  "Klaviyo Abandoned Cart Recovery": {
    summary:
      "Multi-touch abandoned cart recovery with personalized AI copy and progressive incentives. A cart abandon trigger enriches the customer from HubSpot, segments by value, generates AI-personalized copy, sends a reminder email, follows up with social proof after a delay, conditionally offers an incentive, and logs touch attribution data to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "E-commerce Platform / Klaviyo",
        event: "cart.abandoned",
        howItStarts:
          "A customer abandons their shopping cart, triggering a multi-touch recovery sequence with personalized messaging and progressive incentives",
        setup:
          "Connect your e-commerce platform or Klaviyo to POST cart abandonment events (customer email, cart items, cart value, session data) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Customer enrichment (HubSpot)
      hubspotNode(
        "2",
        "Enrich customer from HubSpot",
        280,
        "GET",
        "objects/contacts/{customer_email}?idProperty=email&properties=firstname,lastname,total_revenue,num_associated_deals,hs_lifecyclestage",
        [],
        [
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.total_revenue",
            customName: "lifetime_value",
            type: "number",
          },
          {
            name: "properties.num_associated_deals",
            customName: "past_orders",
            type: "number",
          },
          {
            name: "properties.hs_lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 3. Value segmentation
      n("3", "runCode", "Segment by customer value", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment customer by value for personalization depth
const payload = JSON.parse(inputs.webhook_payload || '{}');
const ltv = parseFloat(inputs.lifetime_value) || 0;
const pastOrders = parseInt(inputs.past_orders) || 0;
const cartValue = parseFloat(payload.total_price || payload.cart_total) || 0;
const customerEmail = payload.email || payload.customer_email || "";
const itemNames = (payload.line_items || payload.items || []).map(i => i.title || i.name).join(", ") || "your items";
let segment, incentiveEligible, maxDiscount;
if (ltv >= 1000 || pastOrders >= 10) {
  segment = "vip";
  incentiveEligible = "yes";
  maxDiscount = 15;
} else if (ltv >= 200 || pastOrders >= 3) {
  segment = "returning";
  incentiveEligible = "yes";
  maxDiscount = 10;
} else {
  segment = "new";
  incentiveEligible = cartValue >= 75 ? "yes" : "no";
  maxDiscount = 5;
}
return {
  customer_email: customerEmail,
  cart_value: cartValue,
  item_names: itemNames,
  value_segment: segment,
  incentive_eligible: incentiveEligible,
  max_discount_pct: maxDiscount
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "cart_value", type: "number" },
            { name: "item_names", type: "string" },
            { name: "value_segment", type: "string" },
            { name: "incentive_eligible", type: "string" },
            { name: "max_discount_pct", type: "number" },
          ],
        },
      }),

      // 4. AI personalized copy
      aiNode(
        "4",
        "AI generate personalized recovery copy",
        760,
        "You are an e-commerce copywriter specializing in cart recovery. Write a warm, personalized recovery email for the given customer segment. VIP customers: acknowledge loyalty, skip urgency tactics. Returning: reference past satisfaction. New: focus on product value and social proof. Never use manipulative urgency or fake scarcity. Respond JSON: {reminder_subject: string, reminder_body: string, social_proof_subject: string, social_proof_body: string, incentive_subject: string, incentive_body: string}",
        "Customer: {first_name} {last_name} | Segment: {value_segment} | Cart: {item_names} (${cart_value}) | LTV: ${lifetime_value} | Past orders: {past_orders} | Max discount: {max_discount_pct}%",
        [
          { field: "reminder_subject", as: "reminder_subject", type: "string" },
          { field: "reminder_body", as: "reminder_body", type: "string" },
          {
            field: "social_proof_subject",
            as: "social_proof_subject",
            type: "string",
          },
          {
            field: "social_proof_body",
            as: "social_proof_body",
            type: "string",
          },
          {
            field: "incentive_subject",
            as: "incentive_subject",
            type: "string",
          },
          { field: "incentive_body", as: "incentive_body", type: "string" },
        ],
      ),

      // 5. Reminder email (Touch 1)
      emailNode(
        "5",
        "Touch 1: Cart reminder email",
        1000,
        "{customer_email}",
        "{reminder_subject}",
        "Hi {first_name},\\n\\n{reminder_body}\\n\\nComplete your order: {cart_recovery_url}\\n\\nBest,\\nThe Team",
      ),

      // 6. Social proof email delay (Touch 2)
      n("6", "delayAction", "Wait 24h for Touch 2", 1240, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),

      // 7. Incentive condition
      n("7", "condition", "Eligible for incentive?", 1480, {
        conditionData: [
          {
            variable: "incentive_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Touch attribution logging (Sheets)
      sheetsNode(
        "8",
        "Log recovery touches to Sheets",
        1720,
        "CartRecovery",
        '[["{customer_email}","{first_name} {last_name}","{value_segment}","{cart_value}","{item_names}","{lifetime_value}","{past_orders}","{incentive_eligible}","{max_discount_pct}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // eligible -> log with incentive flag
      e("7", "8", "output_2"), // not eligible -> log without
    ],
  },

  // ===================================================================
  // 417. KLAVIYO POST-PURCHASE FOLLOW-UP (9 nodes)
  // ===================================================================

  "Klaviyo Post-Purchase Follow-Up": {
    summary:
      "Orchestrates a complete post-purchase journey from confirmation through cross-sell. An order trigger sends a confirmation email, waits for shipping, sends shipment and delivery notices, delivers usage tips, requests a review, and closes with a cross-sell email — all logged for journey performance tracking in Sheets.",
    nodes: [
      trigger("Webhook: New order placed", "webhook_app", {
        source: "E-commerce Platform / Klaviyo",
        event: "order.created",
        howItStarts:
          "A new order is placed, initiating the full post-purchase follow-up journey covering confirmation through cross-sell",
        setup:
          "Connect your e-commerce platform to POST order events (order ID, customer email, customer name, items, total, shipping address) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Confirmation email
      emailNode(
        "2",
        "Send order confirmation email",
        280,
        "{customer_email}",
        "Order confirmed! Your order #{order_id} is on its way",
        "Hi {customer_name},\\n\\nThank you for your order! Here are the details:\\n\\nOrder: #{order_id}\\nItems: {item_names}\\nTotal: ${order_total}\\n\\nWe will send you a shipping notification as soon as your order ships.\\n\\nThank you for choosing us!",
      ),

      // 3. Shipping webhook delay
      n("3", "delayAction", "Wait for shipping (2 days avg)", 520, {
        delayAction: { time: 2, timeFormat: "days" },
      }),

      // 4. Shipment notice
      emailNode(
        "4",
        "Send shipment notification email",
        760,
        "{customer_email}",
        "Your order #{order_id} has shipped!",
        "Hi {customer_name},\\n\\nGreat news — your order #{order_id} is on its way!\\n\\nItems: {item_names}\\nTracking: {tracking_url}\\n\\nEstimated delivery: {estimated_delivery}\\n\\nThank you for your patience!",
      ),

      // 5. Delivery delay
      n("5", "delayAction", "Wait for delivery (5 days avg)", 1000, {
        delayAction: { time: 5, timeFormat: "days" },
      }),

      // 6. Usage tips email
      emailNode(
        "6",
        "Send usage tips email",
        1240,
        "{customer_email}",
        "Getting the most from your {product_category} purchase",
        "Hi {customer_name},\\n\\nWe hope you are enjoying your recent purchase!\\n\\nHere are some tips to get the most out of your {item_names}:\\n\\n{usage_tips_url}\\n\\nHave questions? Our support team is always here to help.\\n\\nEnjoy!",
      ),

      // 7. Review request delay
      n("7", "delayAction", "Wait 7 days before review request", 1480, {
        delayAction: { time: 7, timeFormat: "days" },
      }),

      // 8. Cross-sell email
      emailNode(
        "8",
        "Send cross-sell recommendation email",
        1720,
        "{customer_email}",
        "Customers who bought {product_category} also loved these",
        "Hi {customer_name},\\n\\nBased on your recent purchase of {item_names}, we thought you might also love:\\n\\n{cross_sell_recommendations_url}\\n\\nExplore more: {shop_url}\\n\\nHappy shopping!",
      ),

      // 9. Journey performance logging (Sheets)
      sheetsNode(
        "9",
        "Log journey performance to Sheets",
        1960,
        "PostPurchaseJourney",
        '[["{customer_email}","{customer_name}","{order_id}","{order_total}","{item_names}","{product_category}","confirmation_sent","shipment_sent","tips_sent","crosssell_sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 418. KLAVIYO PRICE DROP ALERT (8 nodes)
  // ===================================================================

  "Klaviyo Price Drop Alert": {
    summary:
      "Alerts interested customers when products they browsed drop in price. A price reduction trigger pulls the interest cohort from HubSpot, calculates savings via runCode, generates AI price-drop messaging, sends email alerts, escalates to SMS for major drops, tracks conversions, and logs threshold tuning data to Sheets.",
    nodes: [
      trigger("Webhook: Product price reduced", "webhook_app", {
        source: "E-commerce Platform / Pricing Engine",
        event: "product.price_reduced",
        howItStarts:
          "A product price is reduced (sale, markdown, price adjustment), triggering alerts to customers who previously browsed or wishlisted that product",
        setup:
          "Connect your e-commerce platform or pricing engine to POST price change events (product ID, product name, old price, new price, category) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Interest cohort pull (HubSpot)
      hubspotNode(
        "2",
        "Pull interested contacts from HubSpot",
        280,
        "POST",
        "objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"browsed_products","operator":"CONTAINS_TOKEN","value":"{product_id}"}]}]',
          },
          {
            key: "properties",
            value: '["email","firstname","lastname","phone"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          {
            name: "results",
            customName: "interested_contacts",
            type: "string",
          },
          { name: "total", customName: "cohort_size", type: "number" },
        ],
      ),

      // 3. Savings calculation (runCode)
      n("3", "runCode", "Calculate savings and drop severity", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate savings amount and classify drop severity
const payload = JSON.parse(inputs.webhook_payload || '{}');
const productName = payload.product_name || payload.title || "Product";
const productId = payload.product_id || payload.id || "";
const oldPrice = parseFloat(payload.old_price || payload.compare_at_price) || 0;
const newPrice = parseFloat(payload.new_price || payload.price) || 0;
const savings = Math.round((oldPrice - newPrice) * 100) / 100;
const savingsPct = oldPrice > 0 ? Math.round((savings / oldPrice) * 100) : 0;
const category = payload.category || payload.product_type || "General";
let dropSeverity;
if (savingsPct >= 30) dropSeverity = "major";
else if (savingsPct >= 15) dropSeverity = "moderate";
else dropSeverity = "minor";
const cohortSize = parseInt(inputs.cohort_size) || 0;
return {
  product_name: productName,
  product_id: productId,
  old_price: oldPrice,
  new_price: newPrice,
  savings_amount: savings,
  savings_pct: savingsPct,
  category: category,
  drop_severity: dropSeverity,
  is_major_drop: dropSeverity === "major" ? "yes" : "no",
  cohort_size: cohortSize
};`,
          outputVariables: [
            { name: "product_name", type: "string" },
            { name: "product_id", type: "string" },
            { name: "old_price", type: "number" },
            { name: "new_price", type: "number" },
            { name: "savings_amount", type: "number" },
            { name: "savings_pct", type: "number" },
            { name: "category", type: "string" },
            { name: "drop_severity", type: "string" },
            { name: "is_major_drop", type: "string" },
            { name: "cohort_size", type: "number" },
          ],
        },
      }),

      // 4. AI price-drop messaging
      aiNode(
        "4",
        "AI generate price-drop messaging",
        760,
        "You are an e-commerce marketing copywriter. Write a compelling but honest price drop alert. Mention the exact savings amount and percentage. For major drops, add urgency about limited-time pricing. For minor drops, position as a helpful heads-up. Never fabricate scarcity. Respond JSON: {email_subject: string, email_body: string, sms_message: string}",
        "Product: {product_name} | Category: {category} | Was: ${old_price} -> Now: ${new_price} | Save: ${savings_amount} ({savings_pct}%) | Drop severity: {drop_severity} | Cohort size: {cohort_size} interested customers",
        [
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "sms_message", as: "sms_message", type: "string" },
        ],
      ),

      // 5. Email alert
      emailNode(
        "5",
        "Send price drop email alert",
        1000,
        "{customer_email}",
        "{email_subject}",
        "{email_body}\\n\\nShop now: {product_url}",
      ),

      // 6. SMS for major drops (Twilio)
      n("6", "condition", "Is this a major price drop?", 1240, {
        conditionData: [
          {
            variable: "is_major_drop",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. SMS send for major drops
      smsNode(
        "7",
        "SMS: Major price drop alert",
        1480,
        "{customer_phone}",
        "{sms_message}",
        460,
      ),

      // 8. Threshold tuning logging (Sheets)
      sheetsNode(
        "8",
        "Log price drop to Sheets",
        1720,
        "PriceDropAlerts",
        '[["{product_name}","{product_id}","{old_price}","{new_price}","{savings_amount}","{savings_pct}","{drop_severity}","{cohort_size}","{category}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // major drop -> SMS
      e("6", "8", "output_2"), // not major -> skip SMS
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 419. LISTING PERFORMANCE ANALYTICS (7 nodes)
  // ===================================================================

  "Listing Performance Analytics": {
    summary:
      "Analyzes listing performance across channels and surfaces optimization opportunities. A daily schedule pulls multi-channel metrics, normalizes data via runCode, uses AI to identify underperformers, generates optimization suggestions, alerts merchandising via Slack, and updates a cross-channel dashboard in Sheets.",
    nodes: [
      trigger("Daily listing performance schedule", "schedule", {
        schedule: "Every day at 7:00 AM UTC",
        howItStarts:
          "Runs daily to pull listing performance metrics from all sales channels, identify underperformers, and generate optimization recommendations",
        setup:
          "Set the cron schedule for daily analytics. Ensure API credentials are configured for all sales channels (Shopify, Amazon, etc.).",
        inputFields: [],
      }),

      // 2. Multi-channel metrics pull
      n("2", "hitApi", "Pull multi-channel metrics", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.channelengine.net/v2/reports/performance?startDate={yesterday_date}&endDate={today_date}",
          headers: [
            { key: "apikey", value: "{channel_engine_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "Content", customName: "channel_metrics", type: "string" },
            {
              name: "TotalCount",
              customName: "total_listings",
              type: "number",
            },
          ],
        },
      }),

      // 3. Normalization (runCode)
      n("3", "runCode", "Normalize metrics across channels", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize performance metrics across all channels
const metrics = JSON.parse(inputs.channel_metrics || '[]');
const normalized = metrics.map(m => {
  const views = parseInt(m.views || m.page_views || m.impressions) || 0;
  const clicks = parseInt(m.clicks || m.sessions) || 0;
  const orders = parseInt(m.orders || m.conversions) || 0;
  const revenue = parseFloat(m.revenue || m.sales) || 0;
  const ctr = views > 0 ? Math.round((clicks / views) * 10000) / 100 : 0;
  const convRate = clicks > 0 ? Math.round((orders / clicks) * 10000) / 100 : 0;
  const avgOrderValue = orders > 0 ? Math.round(revenue / orders * 100) / 100 : 0;
  return {
    product_id: m.product_id || m.sku,
    product_name: m.product_name || m.title,
    channel: m.channel_name || m.channel,
    views, clicks, orders, revenue,
    ctr, conv_rate: convRate, aov: avgOrderValue,
    performance_score: Math.round((ctr * 0.3 + convRate * 0.5 + (revenue > 100 ? 20 : revenue * 0.2)) * 100) / 100
  };
});
const underperformers = normalized.filter(n => n.performance_score < 10 && n.views > 50);
return {
  normalized_data: JSON.stringify(normalized.slice(0, 30)),
  underperformer_count: underperformers.length,
  underperformer_data: JSON.stringify(underperformers.slice(0, 15)),
  total_revenue: normalized.reduce((sum, n) => sum + n.revenue, 0),
  avg_conv_rate: normalized.length > 0 ? Math.round(normalized.reduce((sum, n) => sum + n.conv_rate, 0) / normalized.length * 100) / 100 : 0
};`,
          outputVariables: [
            { name: "normalized_data", type: "string" },
            { name: "underperformer_count", type: "number" },
            { name: "underperformer_data", type: "string" },
            { name: "total_revenue", type: "number" },
            { name: "avg_conv_rate", type: "number" },
          ],
        },
      }),

      // 4. AI underperformer identification
      aiNode(
        "4",
        "AI identify underperformers",
        760,
        "You are an e-commerce merchandising analyst. Analyze the listing performance data and identify which products are underperforming relative to their potential. For each underperformer, diagnose the likely issue (poor images, bad title, wrong pricing, wrong category, low visibility) and suggest specific optimizations. Respond JSON: {underperformer_summary: string, top_issues: string, optimization_suggestions: string, quick_wins: string, estimated_revenue_impact: string}",
        "Total listings: {total_listings} | Underperformers: {underperformer_count} | Total revenue: ${total_revenue} | Avg conv rate: {avg_conv_rate}% | Underperformer data: {underperformer_data} | All data sample: {normalized_data}",
        [
          {
            field: "underperformer_summary",
            as: "underperformer_summary",
            type: "string",
          },
          { field: "top_issues", as: "top_issues", type: "string" },
          {
            field: "optimization_suggestions",
            as: "optimization_suggestions",
            type: "string",
          },
          { field: "quick_wins", as: "quick_wins", type: "string" },
          {
            field: "estimated_revenue_impact",
            as: "estimated_revenue_impact",
            type: "string",
          },
        ],
      ),

      // 5. Optimization suggestions (combined with AI output)
      slackNode(
        "5",
        "Slack: Merchandising optimization alert",
        1000,
        "DAILY LISTING PERFORMANCE REPORT\\nTotal listings: {total_listings} | Revenue: ${total_revenue}\\nAvg conversion rate: {avg_conv_rate}% | Underperformers: {underperformer_count}\\n\\nTop issues: {top_issues}\\n\\nQuick wins: {quick_wins}\\n\\nOptimizations: {optimization_suggestions}\\n\\nEstimated revenue impact: {estimated_revenue_impact}",
      ),

      // 6. Merchandising Slack alert (detailed channel for merch team)
      slackNode(
        "6",
        "Slack: Detailed underperformer list",
        1240,
        "UNDERPERFORMER DETAILS\\n{underperformer_summary}",
      ),

      // 7. Cross-channel dashboard (Sheets)
      sheetsNode(
        "7",
        "Update cross-channel dashboard in Sheets",
        1480,
        "ListingPerformance",
        '[["{total_listings}","{total_revenue}","{avg_conv_rate}","{underperformer_count}","{top_issues}","{quick_wins}","{estimated_revenue_impact}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 420. LISTING QA PIPELINE (9 nodes)
  // ===================================================================

  "Listing QA Pipeline": {
    summary:
      "Validates product listings for quality before syndication. A product create/update trigger runs attribute validation, AI content quality scoring, a combined pass/fail condition, auto-passes clean listings, auto-holds broken ones, alerts the content team on Slack, checks syndication block conditions, and logs QA audit history to Sheets.",
    nodes: [
      trigger("Webhook: Product created or updated", "webhook_app", {
        source: "Shopify / PIM System",
        event: "product.create_or_update",
        howItStarts:
          "A product listing is created or updated in the product catalog, triggering automated quality assurance before it can be syndicated to sales channels",
        setup:
          "Connect your Shopify or PIM system to POST product create/update events (product data including title, description, images, variants, price, tags) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Attribute validation (runCode)
      n("2", "runCode", "Validate listing attributes", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate product listing attributes for completeness and quality
const payload = JSON.parse(inputs.webhook_payload || '{}');
const product = payload.product || payload;
const title = product.title || "";
const description = product.body_html || product.description || "";
const images = product.images || [];
const variants = product.variants || [];
const tags = product.tags || "";
const productType = product.product_type || "";
const issues = [];
// Title checks
if (!title || title.length < 10) issues.push("Title too short (min 10 chars)");
if (title.length > 150) issues.push("Title too long (max 150 chars)");
if (title === title.toUpperCase()) issues.push("Title is all caps");
// Description checks
const descText = description.replace(/<[^>]*>/g, '');
if (!descText || descText.length < 50) issues.push("Description too short (min 50 chars)");
// Image checks
if (images.length === 0) issues.push("No images");
else if (images.length < 3) issues.push("Fewer than 3 images recommended");
// Variant checks
if (variants.length === 0) issues.push("No variants/pricing");
const hasPrice = variants.some(v => parseFloat(v.price) > 0);
if (!hasPrice) issues.push("No price set");
// Category
if (!productType) issues.push("No product type/category");
if (!tags) issues.push("No tags");
const issueCount = issues.length;
const passesValidation = issueCount === 0;
return {
  product_id: product.id || "",
  product_title: title,
  image_count: images.length,
  variant_count: variants.length,
  issue_list: issues.join("; ") || "none",
  issue_count: issueCount,
  passes_validation: passesValidation ? "yes" : "no",
  description_length: descText.length,
  product_type: productType
};`,
          outputVariables: [
            { name: "product_id", type: "string" },
            { name: "product_title", type: "string" },
            { name: "image_count", type: "number" },
            { name: "variant_count", type: "number" },
            { name: "issue_list", type: "string" },
            { name: "issue_count", type: "number" },
            { name: "passes_validation", type: "string" },
            { name: "description_length", type: "number" },
            { name: "product_type", type: "string" },
          ],
        },
      }),

      // 3. AI content quality scoring
      aiNode(
        "3",
        "AI score content quality",
        520,
        "You are an e-commerce content quality evaluator. Score the product listing on: SEO readiness (title keywords, description depth), conversion potential (benefit-oriented copy, clear specs), and brand consistency. Give an overall quality score 0-100. Respond JSON: {quality_score: number, seo_score: number, conversion_score: number, brand_score: number, quality_notes: string, improvement_suggestions: string, content_grade: string}",
        "Product: {product_title} | Type: {product_type} | Description length: {description_length} chars | Images: {image_count} | Variants: {variant_count} | Validation issues: {issue_list} | Issue count: {issue_count}",
        [
          { field: "quality_score", as: "quality_score", type: "number" },
          { field: "seo_score", as: "seo_score", type: "number" },
          { field: "conversion_score", as: "conversion_score", type: "number" },
          { field: "quality_notes", as: "quality_notes", type: "string" },
          {
            field: "improvement_suggestions",
            as: "improvement_suggestions",
            type: "string",
          },
          { field: "content_grade", as: "content_grade", type: "string" },
        ],
      ),

      // 4. Combined pass/fail condition
      n("4", "condition", "Passes QA (score >= 70 and no issues)?", 760, {
        conditionData: [
          {
            variable: "quality_score",
            value: "70",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "passes_validation",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Auto-pass clean listings
      shopifyNode(
        "5",
        "Auto-pass: Publish listing",
        1000,
        "PUT",
        "products/{product_id}.json",
        [
          {
            key: "product",
            value:
              '{"id":"{product_id}","status":"active","tags":"{existing_tags},qa_passed"}',
          },
        ],
        [
          {
            name: "product.id",
            customName: "published_product_id",
            type: "string",
          },
          {
            name: "product.status",
            customName: "product_status",
            type: "string",
          },
        ],
      ),

      // 6. Auto-hold broken listings
      shopifyNode(
        "6",
        "Auto-hold: Draft listing",
        1000,
        "PUT",
        "products/{product_id}.json",
        [
          {
            key: "product",
            value:
              '{"id":"{product_id}","status":"draft","tags":"{existing_tags},qa_failed"}',
          },
        ],
        [
          { name: "product.id", customName: "held_product_id", type: "string" },
          { name: "product.status", customName: "held_status", type: "string" },
        ],
        460,
      ),

      // 7. Content team Slack alert
      slackNode(
        "7",
        "Slack: Content team QA alert",
        1240,
        "LISTING QA RESULT\\nProduct: {product_title} (ID: {product_id})\\nQA Score: {quality_score}/100 | Grade: {content_grade}\\nSEO: {seo_score} | Conversion: {conversion_score}\\nValidation issues: {issue_list}\\nImprovements: {improvement_suggestions}",
      ),

      // 8. Syndication block condition
      n("8", "condition", "Score below syndication threshold (< 50)?", 1480, {
        conditionData: [
          {
            variable: "quality_score",
            value: "50",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. QA audit history (Sheets)
      sheetsNode(
        "9",
        "Log QA audit to Sheets",
        1720,
        "ListingQA",
        '[["{product_id}","{product_title}","{product_type}","{quality_score}","{content_grade}","{seo_score}","{conversion_score}","{issue_count}","{issue_list}","{improvement_suggestions}","{passes_validation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // passes QA -> publish
      e("4", "6", "output_2"), // fails QA -> hold as draft
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // below syndication threshold -> log as blocked
      e("8", "9", "output_2"), // above threshold -> log as passed/held
    ],
  },

  // ===================================================================
  // 421. LOW STOCK ALERT & REORDER (8 nodes)
  // ===================================================================

  "Low Stock Alert & Reorder": {
    summary:
      "Detects low stock, calculates reorder quantities, gets approval for expensive orders, and automates supplier communication. A stock threshold trigger runs reorder calculations, alerts ops on Slack, routes expensive orders through approval, sends supplier PO emails, updates storefront indicators in Shopify, logs reorders to Sheets, and tracks stockout avoidance metrics.",
    nodes: [
      trigger("Webhook: Stock below threshold", "webhook_app", {
        source: "Shopify / Inventory System",
        event: "inventory.below_threshold",
        howItStarts:
          "An inventory item drops below its configured reorder threshold, triggering automatic reorder calculation and supplier notification",
        setup:
          "Connect your inventory system to POST low-stock events (product ID, SKU, product name, current quantity, reorder point, unit cost, supplier info) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Reorder quantity calculation (runCode)
      n("2", "runCode", "Calculate optimal reorder quantity", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate optimal reorder quantity based on velocity and lead time
const payload = JSON.parse(inputs.webhook_payload || '{}');
const sku = payload.sku || "";
const productName = payload.product_name || payload.title || "Unknown";
const currentQty = parseInt(payload.available || payload.current_quantity) || 0;
const unitCost = parseFloat(payload.unit_cost) || 10;
const avgDailySales = parseFloat(payload.avg_daily_sales) || 3;
const leadTimeDays = parseInt(payload.lead_time_days) || 14;
const safetyStockDays = 7;
// EOQ-inspired calculation
const safetyStock = Math.ceil(avgDailySales * safetyStockDays);
const reorderQty = Math.ceil(avgDailySales * (leadTimeDays + safetyStockDays) * 1.2);
const orderCost = Math.round(reorderQty * unitCost * 100) / 100;
const daysUntilStockout = currentQty > 0 ? Math.floor(currentQty / avgDailySales) : 0;
const isExpensiveOrder = orderCost > 1000;
const supplierEmail = payload.supplier_email || "{default_supplier_email}";
const supplierName = payload.supplier_name || "Supplier";
return {
  sku: sku,
  product_name: productName,
  current_qty: currentQty,
  reorder_qty: reorderQty,
  order_cost: orderCost,
  unit_cost: unitCost,
  days_until_stockout: daysUntilStockout,
  lead_time_days: leadTimeDays,
  is_expensive: isExpensiveOrder ? "yes" : "no",
  supplier_email: supplierEmail,
  supplier_name: supplierName,
  urgency: daysUntilStockout <= 3 ? "critical" : daysUntilStockout <= 7 ? "high" : "normal"
};`,
          outputVariables: [
            { name: "sku", type: "string" },
            { name: "product_name", type: "string" },
            { name: "current_qty", type: "number" },
            { name: "reorder_qty", type: "number" },
            { name: "order_cost", type: "number" },
            { name: "unit_cost", type: "number" },
            { name: "days_until_stockout", type: "number" },
            { name: "lead_time_days", type: "number" },
            { name: "is_expensive", type: "string" },
            { name: "supplier_email", type: "string" },
            { name: "supplier_name", type: "string" },
            { name: "urgency", type: "string" },
          ],
        },
      }),

      // 3. Ops Slack alert
      slackNode(
        "3",
        "Slack: Low stock reorder alert",
        520,
        "LOW STOCK REORDER\\nProduct: {product_name} (SKU: {sku})\\nCurrent stock: {current_qty} | Days until stockout: {days_until_stockout}\\nReorder qty: {reorder_qty} units | Cost: ${order_cost}\\nSupplier: {supplier_name} | Lead time: {lead_time_days} days\\nUrgency: {urgency}",
      ),

      // 4. Expensive reorder approval condition
      n("4", "condition", "Is order over $1000?", 760, {
        conditionData: [
          {
            variable: "is_expensive",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Supplier PO email
      emailNode(
        "5",
        "Email supplier: Purchase order",
        1000,
        "{supplier_email}",
        "Purchase Order: {product_name} (SKU: {sku}) - Qty: {reorder_qty}",
        "Dear {supplier_name},\\n\\nPlease process the following purchase order:\\n\\nProduct: {product_name}\\nSKU: {sku}\\nQuantity: {reorder_qty} units\\nUnit cost: ${unit_cost}\\nTotal: ${order_cost}\\nUrgency: {urgency}\\nDays until stockout: {days_until_stockout}\\n\\nPlease confirm availability and expected ship date within 24 hours.\\n\\nThank you,\\nOperations Team",
      ),

      // 6. Storefront indicator update (Shopify)
      shopifyNode(
        "6",
        "Update storefront stock indicator",
        1240,
        "PUT",
        "products/{product_id}/metafields.json",
        [
          {
            key: "metafield",
            value:
              '{"namespace":"inventory","key":"reorder_status","value":"reorder_placed","type":"single_line_text_field"}',
          },
        ],
        [{ name: "metafield.id", customName: "metafield_id", type: "string" }],
      ),

      // 7. Reorder tracking (Sheets)
      sheetsNode(
        "7",
        "Log reorder to Sheets",
        1480,
        "ReorderTracking",
        '[["{sku}","{product_name}","{current_qty}","{reorder_qty}","{order_cost}","{supplier_name}","{urgency}","{days_until_stockout}","{lead_time_days}","{is_expensive}"]]',
      ),

      // 8. Stockout avoidance metrics
      sheetsNode(
        "8",
        "Log stockout avoidance metrics",
        1720,
        "StockoutMetrics",
        '[["{sku}","{product_name}","{days_until_stockout}","{urgency}","{lead_time_days}","{reorder_qty}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // expensive -> approval gate before PO (approval implied in Slack alert)
      e("4", "5", "output_2"), // not expensive -> send PO directly
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 422. LOW-INVENTORY ALERT OPT-IN (7 nodes)
  // ===================================================================

  "Low-Inventory Alert Opt-In": {
    summary:
      "Manages customer opt-in for low-inventory alerts on specific products. A subscription webhook stores preferences via runCode, monitors stock levels, checks the low-stock condition, sends alert email and SMS notifications, and logs subscription conversion data to Sheets.",
    nodes: [
      trigger("Webhook: Product alert subscription", "webhook_app", {
        source: "E-commerce Storefront / Product Page",
        event: "product.alert_subscribed",
        howItStarts:
          "A customer subscribes to receive low-inventory alerts for a specific product they are interested in (via 'Notify me when low' button on product page)",
        setup:
          "Connect your storefront to POST alert subscription events (customer email, customer phone, product ID, product name, current stock level) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Storage + preference (runCode)
      n("2", "runCode", "Store subscription and check stock", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse subscription and determine if already low stock
const payload = JSON.parse(inputs.webhook_payload || '{}');
const email = payload.email || payload.customer_email || "";
const phone = payload.phone || payload.customer_phone || "";
const customerName = payload.name || payload.customer_name || "Valued Customer";
const productId = payload.product_id || "";
const productName = payload.product_name || payload.title || "Product";
const currentStock = parseInt(payload.current_stock || payload.inventory_quantity) || 50;
const alertThreshold = parseInt(payload.alert_threshold) || 10;
const isAlreadyLow = currentStock <= alertThreshold;
return {
  subscriber_email: email,
  subscriber_phone: phone,
  subscriber_name: customerName,
  product_id: productId,
  product_name: productName,
  current_stock: currentStock,
  alert_threshold: alertThreshold,
  is_already_low: isAlreadyLow ? "yes" : "no"
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "subscriber_phone", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "product_id", type: "string" },
            { name: "product_name", type: "string" },
            { name: "current_stock", type: "number" },
            { name: "alert_threshold", type: "number" },
            { name: "is_already_low", type: "string" },
          ],
        },
      }),

      // 3. Stock monitoring (delay for periodic check)
      n("3", "delayAction", "Monitor stock (check every 6h)", 520, {
        delayAction: { time: 6, timeFormat: "hours" },
      }),

      // 4. Low-stock condition
      n("4", "condition", "Is stock below alert threshold?", 760, {
        conditionData: [
          {
            variable: "is_already_low",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Alert email
      emailNode(
        "5",
        "Send low-stock alert email",
        1000,
        "{subscriber_email}",
        "Stock alert: {product_name} is running low — only {current_stock} left",
        "Hi {subscriber_name},\\n\\nYou asked us to let you know when {product_name} was running low — and that time is now!\\n\\nOnly {current_stock} units remaining.\\n\\nGrab yours before it sells out: {product_url}\\n\\nThank you for your interest!",
      ),

      // 6. SMS alert (Twilio)
      smsNode(
        "6",
        "SMS: Low stock alert",
        1240,
        "{subscriber_phone}",
        "Stock alert! {product_name} is running low ({current_stock} left). Shop now: {product_url}",
      ),

      // 7. Subscription conversion logging (Sheets)
      sheetsNode(
        "7",
        "Log alert subscription to Sheets",
        1480,
        "InventoryAlertSubs",
        '[["{subscriber_email}","{subscriber_name}","{product_id}","{product_name}","{current_stock}","{alert_threshold}","{is_already_low}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // low stock -> send alerts
      e("4", "7", "output_2"), // stock healthy -> just log
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 423. LOW-INVENTORY BROWSE REMINDER (7 nodes)
  // ===================================================================

  "Low-Inventory Browse Reminder": {
    summary:
      "Sends authentic scarcity reminders to browsing customers when items genuinely have low stock. A browse exit trigger checks live Shopify stock, conditionally filters for real low-stock items, generates AI authentic scarcity copy (never fake urgency), sends a recovery email, escalates to SMS for critical items, and logs scarcity-vs-trust balance data to Sheets.",
    nodes: [
      trigger("Webhook: Browse session exit", "webhook_app", {
        source: "E-commerce Analytics / Segment",
        event: "browse.session_exit",
        howItStarts:
          "A customer exits after browsing products, triggering a check of actual inventory levels to determine if genuine scarcity messaging is appropriate",
        setup:
          "Connect your analytics platform to POST browse exit events (visitor email, browsed product IDs, session duration, product names) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Live stock check (Shopify)
      n("2", "runCode", "Check live stock for browsed items", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse browsed items and check stock levels
const payload = JSON.parse(inputs.webhook_payload || '{}');
const email = payload.email || payload.visitor_email || "";
const customerName = payload.name || payload.first_name || "there";
const browsedProducts = payload.products || payload.browsed_items || [];
let lowStockItems = [];
let criticalItems = [];
browsedProducts.forEach(p => {
  const stock = parseInt(p.inventory_quantity || p.available) || 50;
  const name = p.title || p.name || "Product";
  const price = parseFloat(p.price) || 0;
  if (stock <= 3) {
    criticalItems.push({ name, stock, price });
    lowStockItems.push({ name, stock, price });
  } else if (stock <= 15) {
    lowStockItems.push({ name, stock, price });
  }
});
const hasLowStock = lowStockItems.length > 0;
const hasCritical = criticalItems.length > 0;
return {
  customer_email: email,
  customer_name: customerName,
  low_stock_items: lowStockItems.map(i => i.name + " (" + i.stock + " left)").join(", ") || "none",
  low_stock_count: lowStockItems.length,
  critical_count: criticalItems.length,
  has_low_stock: hasLowStock ? "yes" : "no",
  has_critical: hasCritical ? "yes" : "no",
  lowest_stock_amount: lowStockItems.length > 0 ? Math.min(...lowStockItems.map(i => i.stock)) : 999,
  total_browsed: browsedProducts.length
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "low_stock_items", type: "string" },
            { name: "low_stock_count", type: "number" },
            { name: "critical_count", type: "number" },
            { name: "has_low_stock", type: "string" },
            { name: "has_critical", type: "string" },
            { name: "lowest_stock_amount", type: "number" },
            { name: "total_browsed", type: "number" },
          ],
        },
      }),

      // 3. Low-stock condition
      n("3", "condition", "Any browsed items genuinely low?", 520, {
        conditionData: [
          {
            variable: "has_low_stock",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI authentic scarcity copy
      aiNode(
        "4",
        "AI generate authentic scarcity message",
        760,
        "You are an ethical e-commerce copywriter. Write a helpful reminder about items the customer browsed that genuinely have low stock. Be transparent — state the exact stock numbers. Never use fake urgency, countdown timers, or manipulative language. Position as a helpful notification. Respond JSON: {email_subject: string, email_body: string, sms_message: string}",
        "Customer: {customer_name} | Low stock items: {low_stock_items} | Low count: {low_stock_count} | Critical count: {critical_count} | Lowest stock: {lowest_stock_amount} units | Total browsed: {total_browsed}",
        [
          { field: "email_subject", as: "scarcity_subject", type: "string" },
          { field: "email_body", as: "scarcity_body", type: "string" },
          { field: "sms_message", as: "scarcity_sms", type: "string" },
        ],
      ),

      // 5. Browse recovery email
      emailNode(
        "5",
        "Send browse recovery email",
        1000,
        "{customer_email}",
        "{scarcity_subject}",
        "Hi {customer_name},\\n\\n{scarcity_body}\\n\\nBrowse your items: {browse_recovery_url}\\n\\nBest regards",
      ),

      // 6. SMS for critical (Twilio)
      smsNode(
        "6",
        "SMS: Critical stock browse reminder",
        1240,
        "{customer_phone}",
        "{scarcity_sms}",
      ),

      // 7. Scarcity vs trust logging (Sheets)
      sheetsNode(
        "7",
        "Log scarcity messaging to Sheets",
        1480,
        "ScarcityMessaging",
        '[["{customer_email}","{customer_name}","{low_stock_items}","{low_stock_count}","{critical_count}","{lowest_stock_amount}","{total_browsed}","{has_critical}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // has low stock -> generate scarcity message
      e("3", "7", "output_2"), // no low stock -> skip messaging, just log
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 424. LOYALTY ENROLLMENT AFTER FIRST PURCHASE (7 nodes)
  // ===================================================================

  "Loyalty Enrollment After First Purchase": {
    summary:
      "Automatically enrolls first-time buyers into the loyalty program with a welcome bonus. A first order trigger checks for existing loyalty membership, creates the account with bonus points via runCode, generates an AI welcome message, sends a loyalty welcome email with dashboard link, tags the CRM contact in HubSpot, and logs enrollment data to Sheets.",
    nodes: [
      trigger("Webhook: First order completed", "webhook_app", {
        source: "E-commerce Platform / Shopify",
        event: "order.first_completed",
        howItStarts:
          "A customer completes their first order, making them eligible for automatic loyalty program enrollment with a welcome bonus",
        setup:
          "Connect your e-commerce platform to POST first-order events (customer email, customer name, order ID, order total, items purchased) to the workflow URL. Use order count = 1 filter.",
        inputFields: [],
      }),

      // 2. Existing loyalty check condition
      n("2", "condition", "Already enrolled in loyalty?", 280, {
        conditionData: [
          {
            variable: "loyalty_member_id",
            value: "",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3. Account creation + welcome bonus (runCode)
      n("3", "runCode", "Create loyalty account with bonus", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Create loyalty account and calculate welcome bonus
const payload = JSON.parse(inputs.webhook_payload || '{}');
const email = payload.email || payload.customer_email || "";
const customerName = payload.customer_name || payload.first_name || "New Member";
const orderId = payload.order_id || "";
const orderTotal = parseFloat(payload.total_price || payload.order_total) || 0;
// Welcome bonus: 100 base + 1 point per dollar of first order
const baseBonus = 100;
const orderBonus = Math.floor(orderTotal);
const totalWelcomePoints = baseBonus + orderBonus;
const pointsValue = Math.round(totalWelcomePoints * 0.01 * 100) / 100; // 1 cent per point
const memberId = "LYL-" + Date.now().toString(36).toUpperCase();
const tier = "bronze";
return {
  customer_email: email,
  customer_name: customerName,
  first_order_id: orderId,
  first_order_total: orderTotal,
  loyalty_member_id: memberId,
  welcome_points: totalWelcomePoints,
  points_dollar_value: pointsValue,
  loyalty_tier: tier,
  base_bonus: baseBonus,
  order_bonus: orderBonus
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "first_order_id", type: "string" },
            { name: "first_order_total", type: "number" },
            { name: "loyalty_member_id", type: "string" },
            { name: "welcome_points", type: "number" },
            { name: "points_dollar_value", type: "number" },
            { name: "loyalty_tier", type: "string" },
          ],
        },
      }),

      // 4. AI welcome message
      aiNode(
        "4",
        "AI generate loyalty welcome message",
        760,
        "You are a customer loyalty program specialist. Write a warm welcome message for a new loyalty member who just made their first purchase. Highlight their welcome bonus points, explain how the program works simply, and make them excited about future rewards. Keep it concise and friendly. Respond JSON: {welcome_subject: string, welcome_body: string, program_highlights: string}",
        "Member: {customer_name} | Email: {customer_email} | Member ID: {loyalty_member_id} | Welcome points: {welcome_points} (worth ${points_dollar_value}) | Tier: {loyalty_tier} | First order: #{first_order_id} (${first_order_total})",
        [
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
          {
            field: "program_highlights",
            as: "program_highlights",
            type: "string",
          },
        ],
      ),

      // 5. Loyalty email with dashboard link
      emailNode(
        "5",
        "Send loyalty welcome email",
        1000,
        "{customer_email}",
        "{welcome_subject}",
        "Hi {customer_name},\\n\\n{welcome_body}\\n\\n{program_highlights}\\n\\nYour loyalty dashboard: {loyalty_dashboard_url}?member={loyalty_member_id}\\n\\nWelcome aboard!",
      ),

      // 6. CRM loyalty tag (HubSpot)
      hubspotNode(
        "6",
        "Tag contact as loyalty member",
        1240,
        "PATCH",
        "objects/contacts/{customer_email}?idProperty=email",
        [
          {
            key: "properties",
            value:
              '{"loyalty_member":"true","loyalty_member_id":"{loyalty_member_id}","loyalty_tier":"{loyalty_tier}","loyalty_points":"{welcome_points}","loyalty_enrolled_date":"{today_date}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),

      // 7. Enrollment logging (Sheets)
      sheetsNode(
        "7",
        "Log loyalty enrollment to Sheets",
        1480,
        "LoyaltyEnrollments",
        '[["{customer_email}","{customer_name}","{loyalty_member_id}","{loyalty_tier}","{welcome_points}","{points_dollar_value}","{first_order_id}","{first_order_total}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3", "output_1"), // not enrolled (empty member ID) -> create account
      e("2", "7", "output_2"), // already enrolled -> just log
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 425. LOYALTY POINTS CALCULATOR BOT (7 nodes)
  // ===================================================================

  "Loyalty Points Calculator Bot": {
    summary:
      "Conversational bot that answers loyalty points questions, simulates scenarios, and delivers balance summaries. A chat/SMS trigger pulls balance and tier data, generates an AI conversational response, runs scenario simulations, sends an email balance summary, replies via SMS, and logs inquiries to Sheets.",
    nodes: [
      trigger("Webhook: Loyalty points inquiry", "webhook_app", {
        source: "Chat Widget / SMS / Support Portal",
        event: "loyalty.points_inquiry",
        howItStarts:
          "A customer asks a question about their loyalty points (balance check, how to earn more, redemption options, tier status) via chat, SMS, or support portal",
        setup:
          "Connect your chat widget, SMS platform, or support portal to POST loyalty inquiries (customer email, message text, channel) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Balance + tier pull
      n("2", "hitApi", "Pull loyalty balance and tier", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.loyaltyprogram.example.com/v1/members/{customer_email}/balance",
          headers: [
            { key: "Authorization", value: "Bearer {loyalty_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "points_balance",
              customName: "points_balance",
              type: "number",
            },
            { name: "tier", customName: "current_tier", type: "string" },
            {
              name: "points_to_next_tier",
              customName: "points_to_next_tier",
              type: "number",
            },
            {
              name: "lifetime_points",
              customName: "lifetime_points",
              type: "number",
            },
            {
              name: "expiring_points",
              customName: "expiring_points",
              type: "number",
            },
            { name: "expiry_date", customName: "expiry_date", type: "string" },
            { name: "member_name", customName: "member_name", type: "string" },
            {
              name: "member_phone",
              customName: "member_phone",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI conversational response
      aiNode(
        "3",
        "AI generate conversational response",
        520,
        "You are a friendly loyalty program assistant. Answer the customer question conversationally based on their actual balance and tier data. Be specific with numbers. If they ask about earning, explain the earning rates for their tier. If about redemption, show options. If about status, celebrate their progress. Respond JSON: {chat_response: string, email_summary: string, sms_response: string, question_category: string}",
        "Question: {webhook_payload} | Member: {member_name} | Balance: {points_balance} pts | Tier: {current_tier} | To next tier: {points_to_next_tier} pts | Lifetime: {lifetime_points} pts | Expiring: {expiring_points} pts by {expiry_date}",
        [
          { field: "chat_response", as: "chat_response", type: "string" },
          { field: "email_summary", as: "email_summary", type: "string" },
          { field: "sms_response", as: "sms_response", type: "string" },
          {
            field: "question_category",
            as: "question_category",
            type: "string",
          },
        ],
      ),

      // 4. Scenario simulation (runCode)
      n("4", "runCode", "Simulate points scenarios", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulate common loyalty scenarios for the member
const balance = parseInt(inputs.points_balance) || 0;
const tier = inputs.current_tier || "bronze";
const toNextTier = parseInt(inputs.points_to_next_tier) || 500;
const tierMultipliers = { bronze: 1, silver: 1.25, gold: 1.5, platinum: 2.0 };
const multiplier = tierMultipliers[tier] || 1;
// Scenario: how much to spend to reach next tier
const spendForNextTier = Math.ceil(toNextTier / multiplier);
// Scenario: redemption options at current balance
const redemptionOptions = [];
if (balance >= 100) redemptionOptions.push("$" + (balance * 0.01).toFixed(2) + " off next order");
if (balance >= 500) redemptionOptions.push("Free shipping for 3 months");
if (balance >= 1000) redemptionOptions.push("$10 gift card");
if (balance >= 2500) redemptionOptions.push("$25 gift card + free shipping");
return {
  spend_for_next_tier: spendForNextTier,
  redemption_options: redemptionOptions.join(" | ") || "Keep earning to unlock rewards",
  earning_rate: multiplier + "x points per dollar",
  points_value_dollars: Math.round(balance * 0.01 * 100) / 100
};`,
          outputVariables: [
            { name: "spend_for_next_tier", type: "number" },
            { name: "redemption_options", type: "string" },
            { name: "earning_rate", type: "string" },
            { name: "points_value_dollars", type: "number" },
          ],
        },
      }),

      // 5. Email balance summary
      emailNode(
        "5",
        "Send balance summary email",
        1000,
        "{customer_email}",
        "Your Loyalty Points Summary — {points_balance} points ({current_tier} tier)",
        "Hi {member_name},\\n\\n{email_summary}\\n\\nYour balance: {points_balance} points (worth ${points_value_dollars})\\nTier: {current_tier} ({earning_rate})\\nTo next tier: {points_to_next_tier} points (spend ${spend_for_next_tier})\\n\\nRedemption options: {redemption_options}\\n\\nExpiring soon: {expiring_points} points by {expiry_date}\\n\\nView your dashboard: {loyalty_dashboard_url}",
      ),

      // 6. SMS quick response (Twilio)
      smsNode(
        "6",
        "SMS: Quick loyalty response",
        1240,
        "{member_phone}",
        "{sms_response}",
      ),

      // 7. Inquiry logging (Sheets)
      sheetsNode(
        "7",
        "Log inquiry to Sheets",
        1480,
        "LoyaltyInquiries",
        '[["{customer_email}","{member_name}","{question_category}","{points_balance}","{current_tier}","{points_to_next_tier}","{expiring_points}","{redemption_options}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 426. LOYALTY POINTS REMINDER (8 nodes)
  // ===================================================================

  "Loyalty Points Reminder": {
    summary:
      "Proactively reminds members about expiring loyalty points with personalized urgency. A scheduled expiry scan pulls balances and expiration dates, selects reminder angles via runCode, generates AI personalized copy, sends reminder emails, escalates to SMS for near-expiry cases, tracks redemptions, and logs cadence tuning data to Sheets.",
    nodes: [
      trigger("Daily expiring points scan", "schedule", {
        schedule: "Every day at 10:00 AM UTC",
        howItStarts:
          "Runs daily to identify loyalty members with points expiring within the next 30 days and send personalized reminder campaigns",
        setup:
          "Set the cron schedule for the daily expiry scan. Ensure the loyalty platform API is configured for member balance lookups.",
        inputFields: [],
      }),

      // 2. Balance + expiration pull
      n("2", "hitApi", "Pull members with expiring points", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.loyaltyprogram.example.com/v1/members/expiring?days_ahead=30&min_points=50",
          headers: [
            { key: "Authorization", value: "Bearer {loyalty_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "members", customName: "expiring_members", type: "string" },
            {
              name: "total_count",
              customName: "expiring_member_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Reminder angle selection (runCode)
      n("3", "runCode", "Select reminder angle per member", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select the best reminder angle for each member batch
const members = JSON.parse(inputs.expiring_members || '[]');
const now = new Date();
const processed = members.map(m => {
  const expiryDate = new Date(m.expiry_date);
  const daysLeft = Math.ceil((expiryDate - now) / 86400000);
  const points = m.expiring_points || 0;
  const value = Math.round(points * 0.01 * 100) / 100;
  let angle, urgencyLevel;
  if (daysLeft <= 3) {
    angle = "last_chance";
    urgencyLevel = "critical";
  } else if (daysLeft <= 7) {
    angle = "expiring_soon";
    urgencyLevel = "high";
  } else if (daysLeft <= 14) {
    angle = "friendly_reminder";
    urgencyLevel = "medium";
  } else {
    angle = "plan_ahead";
    urgencyLevel = "low";
  }
  return {
    email: m.email,
    name: m.name,
    phone: m.phone || "",
    points: points,
    value: value,
    days_left: daysLeft,
    angle: angle,
    urgency: urgencyLevel,
    tier: m.tier || "bronze"
  };
});
const criticalCount = processed.filter(p => p.urgency === "critical").length;
const current = processed[0] || {};
return {
  member_email: current.email || "",
  member_name: current.name || "",
  member_phone: current.phone || "",
  expiring_points: current.points || 0,
  points_value: current.value || 0,
  days_until_expiry: current.days_left || 30,
  reminder_angle: current.angle || "friendly_reminder",
  urgency_level: current.urgency || "low",
  member_tier: current.tier || "bronze",
  is_near_expiry: (current.days_left || 30) <= 7 ? "yes" : "no",
  critical_members: criticalCount,
  total_expiring: processed.length
};`,
          outputVariables: [
            { name: "member_email", type: "string" },
            { name: "member_name", type: "string" },
            { name: "member_phone", type: "string" },
            { name: "expiring_points", type: "number" },
            { name: "points_value", type: "number" },
            { name: "days_until_expiry", type: "number" },
            { name: "reminder_angle", type: "string" },
            { name: "urgency_level", type: "string" },
            { name: "member_tier", type: "string" },
            { name: "is_near_expiry", type: "string" },
            { name: "critical_members", type: "number" },
            { name: "total_expiring", type: "number" },
          ],
        },
      }),

      // 4. AI personalized copy
      aiNode(
        "4",
        "AI generate expiry reminder copy",
        760,
        "You are a loyalty program engagement specialist. Write a personalized points expiry reminder matching the selected angle: last_chance (urgent, direct), expiring_soon (motivated, clear), friendly_reminder (warm, helpful), plan_ahead (casual, suggestive). Include the exact points value and days remaining. Never be pushy for low-urgency angles. Respond JSON: {email_subject: string, email_body: string, sms_message: string, redemption_suggestion: string}",
        "Member: {member_name} | Tier: {member_tier} | Expiring: {expiring_points} pts (${points_value}) | Days left: {days_until_expiry} | Angle: {reminder_angle} | Urgency: {urgency_level}",
        [
          { field: "email_subject", as: "reminder_subject", type: "string" },
          { field: "email_body", as: "reminder_body", type: "string" },
          { field: "sms_message", as: "reminder_sms", type: "string" },
          {
            field: "redemption_suggestion",
            as: "redemption_suggestion",
            type: "string",
          },
        ],
      ),

      // 5. Reminder email
      emailNode(
        "5",
        "Send points expiry reminder email",
        1000,
        "{member_email}",
        "{reminder_subject}",
        "Hi {member_name},\\n\\n{reminder_body}\\n\\n{redemption_suggestion}\\n\\nRedeem now: {loyalty_dashboard_url}\\n\\nHappy redeeming!",
      ),

      // 6. Urgent SMS for near-expiry (Twilio)
      n("6", "condition", "Near expiry (7 days or less)?", 1240, {
        conditionData: [
          {
            variable: "is_near_expiry",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. SMS send for near-expiry
      smsNode(
        "7",
        "SMS: Urgent points expiry reminder",
        1480,
        "{member_phone}",
        "{reminder_sms}",
        460,
      ),

      // 8. Cadence tuning logging (Sheets)
      sheetsNode(
        "8",
        "Log reminder to Sheets",
        1720,
        "PointsExpiryReminders",
        '[["{member_email}","{member_name}","{member_tier}","{expiring_points}","{points_value}","{days_until_expiry}","{reminder_angle}","{urgency_level}","{total_expiring}","{critical_members}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // near expiry -> send SMS
      e("6", "8", "output_2"), // not near -> skip SMS
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 427. MARKET EXPANSION LISTING CREATOR (9 nodes)
  // ===================================================================

  "Market Expansion Listing Creator": {
    summary:
      "Automates product listing creation for new market expansions including translation, localization, and compliance. A market rollout trigger pulls the source listing, AI translates and localizes content, runCode validates compliance, converts local pricing, drafts a Shopify listing, optimizes keywords, sends to a native speaker approval gate, and logs the launch to Sheets.",
    nodes: [
      trigger("Webhook: Market expansion rollout", "webhook_app", {
        source: "Market Expansion Dashboard / Product Ops",
        event: "market.expansion_rollout",
        howItStarts:
          "A product is marked for expansion into a new market/locale, triggering automated listing creation with translation, compliance, and localization",
        setup:
          "Connect your market expansion dashboard to POST rollout events (source product ID, target market, target language, target currency, compliance requirements) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Source listing pull
      shopifyNode(
        "2",
        "Pull source listing from Shopify",
        280,
        "GET",
        "products/{source_product_id}.json",
        [],
        [
          { name: "product.title", customName: "source_title", type: "string" },
          {
            name: "product.body_html",
            customName: "source_description",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "source_price",
            type: "string",
          },
          {
            name: "product.product_type",
            customName: "product_type",
            type: "string",
          },
          { name: "product.tags", customName: "source_tags", type: "string" },
          {
            name: "product.images",
            customName: "source_images",
            type: "string",
          },
          {
            name: "product.variants",
            customName: "source_variants",
            type: "string",
          },
        ],
      ),

      // 3. AI translation + localization
      aiNode(
        "3",
        "AI translate and localize listing",
        520,
        "You are a professional e-commerce translator and localizer. Translate the product listing into the target language while adapting it for the local market. Do not just translate literally — localize tone, cultural references, size/measurement units, and selling points for the target audience. Maintain SEO value. Respond JSON: {localized_title: string, localized_description: string, localized_tags: string, localization_notes: string, cultural_adaptations: string, seo_keywords_local: string}",
        "Source title: {source_title} | Source description: {source_description} | Source tags: {source_tags} | Product type: {product_type} | Target market: {target_market} | Target language: {target_language}",
        [
          { field: "localized_title", as: "localized_title", type: "string" },
          {
            field: "localized_description",
            as: "localized_description",
            type: "string",
          },
          { field: "localized_tags", as: "localized_tags", type: "string" },
          {
            field: "localization_notes",
            as: "localization_notes",
            type: "string",
          },
          {
            field: "cultural_adaptations",
            as: "cultural_adaptations",
            type: "string",
          },
          {
            field: "seo_keywords_local",
            as: "seo_keywords_local",
            type: "string",
          },
        ],
      ),

      // 4. Compliance validation (runCode)
      n("4", "runCode", "Validate local compliance requirements", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate product against target market compliance requirements
const payload = JSON.parse(inputs.webhook_payload || '{}');
const targetMarket = payload.target_market || "US";
const targetCurrency = payload.target_currency || "USD";
const sourcePrice = parseFloat(inputs.source_price) || 0;
const complianceReqs = payload.compliance_requirements || [];
// Currency conversion rates (simplified; production would use live rates)
const rates = { USD: 1, EUR: 0.92, GBP: 0.79, CAD: 1.36, AUD: 1.53, JPY: 149.5, MXN: 17.1 };
const rate = rates[targetCurrency] || 1;
const convertedPrice = Math.round(sourcePrice * rate * 100) / 100;
// Round to local pricing convention
let localPrice;
if (targetCurrency === "JPY") localPrice = Math.ceil(convertedPrice / 100) * 100;
else localPrice = Math.ceil(convertedPrice) - 0.01; // e.g., $49.99
const issues = [];
// Basic compliance checks by market
const euMarkets = ["DE", "FR", "IT", "ES", "NL", "BE"];
if (euMarkets.includes(targetMarket)) {
  issues.push("Verify CE marking compliance");
  issues.push("GDPR privacy notice required on listing");
}
if (targetMarket === "CA") {
  issues.push("Bilingual labeling required (EN/FR)");
}
if (targetMarket === "AU") {
  issues.push("Australian Consumer Law compliance check");
}
const passesCompliance = issues.length <= 2; // minor issues are acceptable
return {
  target_market: targetMarket,
  target_currency: targetCurrency,
  source_price: sourcePrice,
  local_price: localPrice,
  exchange_rate: rate,
  compliance_issues: issues.join("; ") || "none",
  compliance_issue_count: issues.length,
  passes_compliance: passesCompliance ? "yes" : "no"
};`,
          outputVariables: [
            { name: "target_market", type: "string" },
            { name: "target_currency", type: "string" },
            { name: "source_price", type: "number" },
            { name: "local_price", type: "number" },
            { name: "exchange_rate", type: "number" },
            { name: "compliance_issues", type: "string" },
            { name: "compliance_issue_count", type: "number" },
            { name: "passes_compliance", type: "string" },
          ],
        },
      }),

      // 5. Local pricing conversion (already done in runCode, create variant)
      n("5", "runCode", "Prepare local listing payload", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare the final listing payload with local pricing and tags
const localPrice = inputs.local_price || 0;
const localTitle = inputs.localized_title || inputs.source_title || "";
const localDesc = inputs.localized_description || inputs.source_description || "";
const localTags = inputs.localized_tags || inputs.source_tags || "";
const seoKeywords = inputs.seo_keywords_local || "";
const allTags = [localTags, seoKeywords, "market_expansion", inputs.target_market].filter(Boolean).join(",");
return {
  listing_title: localTitle,
  listing_description: localDesc,
  listing_price: localPrice,
  listing_tags: allTags,
  listing_ready: "yes"
};`,
          outputVariables: [
            { name: "listing_title", type: "string" },
            { name: "listing_description", type: "string" },
            { name: "listing_price", type: "number" },
            { name: "listing_tags", type: "string" },
            { name: "listing_ready", type: "string" },
          ],
        },
      }),

      // 6. Shopify listing draft
      shopifyNode(
        "6",
        "Create Shopify listing draft",
        1240,
        "POST",
        "products.json",
        [
          {
            key: "product",
            value:
              '{"title":"{listing_title}","body_html":"{listing_description}","product_type":"{product_type}","tags":"{listing_tags}","status":"draft","variants":[{"price":"{listing_price}","currency":"{target_currency}"}]}',
          },
        ],
        [
          { name: "product.id", customName: "new_product_id", type: "string" },
          {
            name: "product.handle",
            customName: "product_handle",
            type: "string",
          },
        ],
      ),

      // 7. Keyword optimization
      aiNode(
        "7",
        "AI optimize local SEO keywords",
        1480,
        "You are a multilingual SEO specialist. Review the localized listing and suggest additional local keywords, meta descriptions, and search optimization improvements for the target market. Consider local search behavior and competitor keywords. Respond JSON: {meta_title: string, meta_description: string, additional_keywords: string, seo_improvements: string}",
        "Market: {target_market} | Language: {target_language} | Title: {listing_title} | Tags: {listing_tags} | Current keywords: {seo_keywords_local} | Product type: {product_type}",
        [
          { field: "meta_title", as: "meta_title", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          {
            field: "additional_keywords",
            as: "additional_keywords",
            type: "string",
          },
          { field: "seo_improvements", as: "seo_improvements", type: "string" },
        ],
      ),

      // 8. Native speaker approval gate
      n("8", "approvalGate", "Native speaker reviews listing", 1720, {
        approvalGateData: {
          message:
            "New market expansion listing for {target_market} ({target_language}):\\n\\nTitle: {listing_title}\\nPrice: {listing_price} {target_currency}\\nCompliance: {compliance_issues}\\nCultural adaptations: {cultural_adaptations}\\nSEO keywords: {seo_keywords_local}\\n\\nPlease review translation quality, cultural accuracy, and local market fit before approving for launch.",
        },
      }),

      // 9. Launch logging (Sheets)
      sheetsNode(
        "9",
        "Log market expansion to Sheets",
        1960,
        "MarketExpansion",
        '[["{source_title}","{listing_title}","{target_market}","{target_language}","{target_currency}","{source_price}","{local_price}","{exchange_rate}","{compliance_issues}","{new_product_id}","{seo_keywords_local}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
